/* Startseite 2026 (Vorschau) — behaviour of the mbb-* sections.

   One file for all modules, loaded with defer by every section that needs it
   (the guard below makes the second tag a no-op). Each module starts on an
   element with data-mbb="<name>"; the theme editor re-runs them on
   shopify:section:load. No library: pointer events, IntersectionObserver,
   requestAnimationFrame.

   Rules shared by all modules
   - Nothing moves by itself with prefers-reduced-motion, off screen, in a
     hidden tab, or while the visitor hovers, focuses or touches the module.
   - Figures stand on a common floor: the visible outline of every cut-out
     is read from its alpha channel (umriss) and the image is placed so that
     exactly that outline has the wanted height (stelle).
   - Anything that moves does so through transform, clip-path or opacity,
     never left/top/width/height: those count as layout shifts (CLS) even
     when they follow the visitor's scrolling. */
(() => {
  if (window.mbbStart) return;
  window.mbbStart = true;

  const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Language of the page (theme.liquid writes request.locale into <html lang>): the
  // texts the script writes itself follow it, everything else comes from Liquid.
  const EN = (document.documentElement.lang || '').toLowerCase().startsWith('en');
  const klemme = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const mische = (a, b, t) => a + (b - a) * t;
  const weich = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const warte = (ms) => new Promise((r) => setTimeout(r, ms));
  // Share of the photo height a single 400% figure takes in the shop's product
  // photos (median of the belt and stage, 03.11.2026: 0.908–0.917).
  const GROESSE_EINZELFIGUR = 0.915;

  /* ── Visible outline of a cut-out photo ─────────────────────────── */
  const umrisse = new Map();
  function geladen(img) {
    if (img.complete && img.naturalWidth) return Promise.resolve();
    return new Promise((r) => {
      img.addEventListener('load', r, { once: true });
      img.addEventListener('error', r, { once: true });
    });
  }
  /** Fractions x0/y0/x1/y1 of the figure inside the image (alpha > 24). */
  function umriss(img) {
    const schluessel = img.currentSrc || img.src || img.dataset.src;
    if (umrisse.has(schluessel)) return umrisse.get(schluessel);
    const p = geladen(img).then(() => {
      if (!img.naturalWidth) return null;
      const b = 160;
      const h = Math.max(1, Math.round((b * img.naturalHeight) / img.naturalWidth));
      const c = document.createElement('canvas');
      c.width = b;
      c.height = h;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, b, h);
      let d;
      try {
        d = ctx.getImageData(0, 0, b, h).data;
      } catch (e) {
        return { x0: 0, y0: 0, x1: 1, y1: 1 };
      }
      let x0 = b, y0 = h, x1 = -1, y1 = -1;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < b; x++) {
          if (d[(y * b + x) * 4 + 3] > 24) {
            if (x < x0) x0 = x;
            if (x > x1) x1 = x;
            if (y < y0) y0 = y;
            if (y > y1) y1 = y;
          }
        }
      }
      if (x1 < 0) return { x0: 0, y0: 0, x1: 1, y1: 1 };
      return { x0: x0 / b, y0: y0 / h, x1: (x1 + 1) / b, y1: (y1 + 1) / h };
    });
    umrisse.set(schluessel, p);
    return p;
  }
  /**
   * The separate figures of a cut-out photo: connected parts of the alpha mask,
   * the largest is the main figure, smaller ones (at least 3 % of its area) stand
   * next to it — the 100 % piece of a 100%+400% set stands in front of the 400 %
   * one and lower in the photo, so the lowest visible point is not the main
   * figure's feet. Fractions of the image, like umriss.
   */
  const teileCache = new Map();
  function figurenteile(img) {
    const schluessel = img.currentSrc || img.src;
    if (teileCache.has(schluessel)) return teileCache.get(schluessel);
    const p = geladen(img).then(() => {
      if (!img.naturalWidth) return null;
      const b = 120;
      const h = Math.max(1, Math.round((b * img.naturalHeight) / img.naturalWidth));
      const c = document.createElement('canvas');
      c.width = b;
      c.height = h;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, b, h);
      let d;
      try {
        d = ctx.getImageData(0, 0, b, h).data;
      } catch (e) {
        return null;
      }
      const marke = new Int32Array(b * h).fill(-1);
      const teile = [];
      for (let s = 0; s < b * h; s++) {
        if (d[s * 4 + 3] <= 24 || marke[s] >= 0) continue;
        const t = { n: 0, x0: b, x1: -1, y0: h, y1: -1 };
        const stapel = [s];
        marke[s] = teile.length;
        while (stapel.length) {
          const q = stapel.pop();
          const x = q % b;
          const y = (q - x) / b;
          t.n++;
          if (x < t.x0) t.x0 = x;
          if (x > t.x1) t.x1 = x;
          if (y < t.y0) t.y0 = y;
          if (y > t.y1) t.y1 = y;
          const nachbarn = [x > 0 ? q - 1 : -1, x < b - 1 ? q + 1 : -1, q - b, q + b];
          for (const r of nachbarn) {
            if (r < 0 || r >= b * h || marke[r] >= 0 || d[r * 4 + 3] <= 24) continue;
            marke[r] = teile.length;
            stapel.push(r);
          }
        }
        teile.push(t);
      }
      if (!teile.length) return null;
      teile.sort((a, z) => z.n - a.n);
      const anteil = (t) => ({ x0: t.x0 / b, x1: (t.x1 + 1) / b, y0: t.y0 / h, y1: (t.y1 + 1) / h });
      return {
        haupt: anteil(teile[0]),
        neben: teile.slice(1).filter((t) => t.n > teile[0].n * 0.03).map(anteil),
      };
    });
    teileCache.set(schluessel, p);
    return p;
  }
  /**
   * Places img so that its visible figure is `hoehe` px tall, centred on
   * `mitte`, standing on `boden` — as a transform of its CSS box (offset*
   * ignore transforms), so moving it never shifts the layout.
   */
  function stelle(img, u, { hoehe, mitte, boden }) {
    const b0 = img.offsetWidth;
    const h0 = img.offsetHeight;
    if (!b0 || !h0) return;
    const s = hoehe / ((u.y1 - u.y0) * h0);
    const links = mitte - ((u.x0 + u.x1) / 2) * b0 * s;
    const oben = boden - u.y1 * h0 * s;
    img.style.transformOrigin = '0 0';
    img.style.transform = `translate(${links - img.offsetLeft}px, ${oben - img.offsetTop}px) scale(${s})`;
    img.dataset.gemessen = '';
  }
  /** Width of the visible figure relative to its visible height. */
  const seitenverhaeltnis = (img, u) => ((u.x1 - u.x0) * img.naturalWidth) / ((u.y1 - u.y0) * img.naturalHeight);
  /** Swaps data-src/data-srcset in (deferred images, see snippets/mbb-figurbild.liquid). */
  function lade(img) {
    if (!img || !img.dataset.src) return;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    delete img.dataset.src;
    delete img.dataset.srcset;
  }
  function beobachte(el, wenn, rand = '0px') {
    const io = new IntersectionObserver((e) => wenn(e[0].isIntersecting), { rootMargin: rand });
    io.observe(el);
    return io;
  }

  const module = {};

  /* ── 1 · Wechselbär ─────────────────────────────────────────────── */
  module.wechselbaer = (root) => {
    // The search field under the headline (narrow screens, operator 03.11.2026)
    // opens the shop's own search in the header — with its suggestions and
    // prices — instead of a second search box in the hero. Opened in the tap
    // itself, so the phone brings up the keyboard; without the header search
    // (or without script) the link leads to the search page.
    const sucheKnopf = root.querySelector('[data-suche-oeffnen]');
    if (sucheKnopf) {
      sucheKnopf.addEventListener('click', (e) => {
        // Header 2026 (sections/mbb-kopf): one search dialog, opened in the tap itself.
        if (window.mbbKopf) {
          e.preventDefault();
          window.mbbKopf.sucheOeffnen();
          return;
        }
        const kopf = [...document.querySelectorAll('details-modal.header__search')].find((m) => m.offsetParent !== null);
        const summary = kopf && kopf.querySelector('summary');
        if (!summary) return;
        e.preventDefault();
        // The modal listens for clicks on the body as soon as it opens; this tap
        // would reach it next and close the search again.
        e.stopPropagation();
        if (!kopf.querySelector('details').hasAttribute('open')) summary.click();
      });
    }

    const dialog = root.querySelector('[data-video-dialog]');
    const videoKnopf = root.querySelector('[data-video-oeffnen]');
    if (dialog && videoKnopf && dialog.showModal) {
      const video = dialog.querySelector('video');
      const drehen = dialog.querySelector('[data-drehen]');
      // Phones (Betreiber 03.11.2026): the video is 16:9, upright its texts are too
      // small to read. Where the browser allows it (Android), full screen turned to
      // landscape; a web page cannot turn an iPhone (no orientation lock there), so
      // a hint to turn the phone, gone once turned or after a few seconds.
      const telefon = window.matchMedia('(hover: none) and (pointer: coarse)');
      const hoch = window.matchMedia('(orientation: portrait)');
      let drehUhr = null;
      let vollbild = false;
      const versteckeDrehen = () => {
        clearTimeout(drehUhr);
        if (drehen) drehen.hidden = true;
      };
      const zeigeDrehen = () => {
        if (!drehen || !hoch.matches || !dialog.open) return;
        drehen.hidden = false;
        clearTimeout(drehUhr);
        drehUhr = setTimeout(versteckeDrehen, 5000);
      };
      const querformat = () => {
        if (!video || !telefon.matches) return;
        if (!document.fullscreenEnabled || !video.requestFullscreen) {
          zeigeDrehen();
          return;
        }
        video
          .requestFullscreen({ navigationUI: 'hide' })
          .then(() => {
            vollbild = true;
            return screen.orientation && screen.orientation.lock ? screen.orientation.lock('landscape') : Promise.reject();
          })
          .catch(() => {
            if (!document.fullscreenElement) zeigeDrehen();
          });
      };
      hoch.addEventListener('change', () => {
        if (!hoch.matches) versteckeDrehen();
      });
      // Leaving full screen (back gesture, button) ends the video like closing the dialog.
      document.addEventListener('fullscreenchange', () => {
        if (document.fullscreenElement || !vollbild) return;
        vollbild = false;
        if (dialog.open) dialog.close();
      });
      videoKnopf.addEventListener('click', () => {
        dialog.showModal();
        if (video) video.play().catch(() => {});
        querformat();
      });
      dialog.addEventListener('close', () => {
        if (video) video.pause();
        versteckeDrehen();
        if (screen.orientation && screen.orientation.unlock) {
          try {
            screen.orientation.unlock();
          } catch (e) {
            /* not locked */
          }
        }
        if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
      });
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) dialog.close();
      });
    }

    const buehne = root.querySelector('[data-buehne]');
    const figuren = [...root.querySelectorAll('[data-figur]')];
    if (!buehne || figuren.length === 0) return;
    const bilder = figuren.map((f) => f.querySelector('img'));
    const nameEl = root.querySelector('[data-info-link]');
    const pfeil = nameEl && nameEl.querySelector('svg');
    const metaEl = root.querySelector('[data-info-meta]');
    const zaehler = root.querySelector('[data-zaehler]');
    const hinweis = root.querySelector('[data-hinweis]');
    const n = figuren.length;
    let akt = 0;
    let naechste = -1;
    let richtung = 1;
    let anteil = 0;
    let lauf = null;
    let sichtbar = false;
    let haelt = false;
    let zug = null;
    let uhr = null;
    // Mouse wiped to the far side: the new design stays (Betreiber 03.11.2026), and
    // the next one waits until the pointer is back on the right.
    let mausWartet = false;
    // Kind of the last pointer pressed on the stage: Safari delivers `click` as a plain
    // MouseEvent without pointerType, a tap would otherwise count as a mouse click.
    let zeiger = '';
    // A click on the picture opens the product (Betreiber 03.11.2026) — only while one
    // bear shows on at least this share of the stage; in between the click keeps
    // the new design as before. Same mark as the waiting mouse above.
    const KLAR = 0.85;

    // Box photos are opaque and fill the stage (CSS): there is no silhouette to stand on the floor.
    const fotos = root.dataset.bildart === 'verpackung';
    const ordne = () => {
      if (fotos) return;
      const b = buehne.clientWidth;
      const h = buehne.clientHeight;
      // Share of the stage height the bear fills (CSS --wb-figur: smaller on wide screens, see the CSS).
      const anteilFigur = parseFloat(getComputedStyle(buehne).getPropertyValue('--wb-figur')) || 0.8;
      bilder.forEach((img) => {
        if (!img || img.dataset.src) return;
        umriss(img).then((u) => u && stelle(img, u, { hoehe: h * anteilFigur, mitte: b / 2, boden: h * 0.94 }));
      });
    };
    new ResizeObserver(ordne).observe(buehne);
    bilder.forEach((img) => img && img.addEventListener('load', ordne));

    // The bear a click would open: the one on the stage, or the arriving one once it
    // covers the stage; -1 while both are clearly visible.
    const gemeint = () => {
      if (naechste < 0 || anteil <= 1 - KLAR) return akt;
      if (anteil >= KLAR) return naechste;
      return -1;
    };
    const zeigeAnteil = (q) => {
      anteil = q;
      buehne.style.setProperty('--kante', richtung > 0 ? 1 - q : q);
      // Hand cursor where the click opens a product, the wipe cursor in between (CSS).
      buehne.classList.toggle('ist-klickbar', gemeint() >= 0);
    };
    const info = () => {
      const f = figuren[akt];
      if (nameEl) {
        nameEl.href = f.dataset.url;
        // Fresh nodes for name, space and arrow: kept nodes would move between a one- and
        // a two-line name and count as layout shift, newly inserted ones do not.
        const zeilen = document.createElement('span');
        // Over 40 characters the name gets a smaller size (CSS) — the same limit as in the section.
        zeilen.className = f.dataset.name.length > 40 ? 'mbb-wb__zeilen ist-lang' : 'mbb-wb__zeilen';
        const text = document.createElement('span');
        text.dataset.infoName = '';
        text.textContent = f.dataset.name;
        zeilen.append(text, ' ', ...(pfeil ? [pfeil.cloneNode(true)] : []));
        nameEl.replaceChildren(zeilen);
      }
      if (metaEl) metaEl.textContent = f.dataset.meta;
      if (zaehler) zaehler.textContent = `${akt + 1} / ${n}`;
    };
    const vorlade = (i) => lade(bilder[(i + n) % n]);
    const weg = () => hinweis && hinweis.classList.add('ist-weg');

    const bereite = (i, r) => {
      const ziel = (i + n) % n;
      if (naechste === ziel && richtung === r) return;
      if (naechste >= 0) figuren[naechste].classList.remove('ist-naechste', 'von-links');
      naechste = ziel;
      richtung = r;
      lade(bilder[ziel]);
      ordne();
      figuren[ziel].classList.toggle('von-links', r < 0);
      figuren[ziel].classList.add('ist-naechste');
      zeigeAnteil(0);
    };
    const uebernimm = () => {
      if (naechste < 0) return;
      figuren[akt].classList.remove('ist-aktiv');
      figuren[naechste].classList.remove('ist-naechste', 'von-links');
      figuren[naechste].classList.add('ist-aktiv');
      akt = naechste;
      naechste = -1;
      buehne.classList.remove('zeigt-kante');
      zeigeAnteil(0);
      info();
      vorlade(akt + 1);
      vorlade(akt - 1);
    };
    const verwirf = () => {
      if (naechste >= 0) figuren[naechste].classList.remove('ist-naechste', 'von-links');
      naechste = -1;
      buehne.classList.remove('zeigt-kante');
    };
    const animiere = (ziel, dauer, fertig) => {
      if (lauf) cancelAnimationFrame(lauf);
      const start = anteil;
      if (ruhig.matches || dauer <= 0) {
        zeigeAnteil(ziel);
        lauf = null;
        fertig();
        return;
      }
      buehne.classList.add('zeigt-kante');
      const t0 = performance.now();
      const schritt = (t) => {
        const q = klemme((t - t0) / dauer);
        zeigeAnteil(mische(start, ziel, weich(q)));
        if (q < 1) lauf = requestAnimationFrame(schritt);
        else {
          lauf = null;
          fertig();
        }
      };
      lauf = requestAnimationFrame(schritt);
    };
    const wechsle = (r, dauer = 650) => {
      if (lauf || n < 2) return;
      bereite(akt + r, r);
      animiere(1, dauer, uebernimm);
    };

    // Self-running change: only visible, untouched, without reduced motion.
    const plane = () => {
      clearTimeout(uhr);
      if (n < 2 || ruhig.matches) return;
      uhr = setTimeout(() => {
        if (sichtbar && !haelt && !zug && !document.hidden && !lauf && naechste < 0) wechsle(1, 1400);
        plane();
      }, 4800);
    };
    beobachte(root, (s) => {
      sichtbar = s;
    });
    plane();
    if ('requestIdleCallback' in window) requestIdleCallback(() => vorlade(1));
    else setTimeout(() => vorlade(1), 1500);

    root.querySelector('[data-weiter]')?.addEventListener('click', () => {
      weg();
      wechsle(1);
      plane();
    });
    root.querySelector('[data-zurueck]')?.addEventListener('click', () => {
      weg();
      wechsle(-1);
      plane();
    });
    root.addEventListener('focusin', () => (haelt = true));
    root.addEventListener('focusout', () => (haelt = false));

    // Mouse: the edge follows the pointer. A click opens the bear that fills the
    // stage (gemeint), otherwise it keeps the new design; Ctrl/Cmd or the middle
    // button open the product in a new tab, as on a link.
    const xAnteil = (e) => {
      const r = buehne.getBoundingClientRect();
      return klemme((e.clientX - r.left) / r.width);
    };
    buehne.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') haelt = true;
    });
    buehne.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || lauf || n < 2) return;
      const x = xAnteil(e);
      if (mausWartet) {
        if (x < 0.85) return;
        mausWartet = false;
      }
      bereite(akt + 1, 1);
      buehne.classList.add('zeigt-kante');
      weg();
      zeigeAnteil(klemme(1 - x, 0, 0.98));
      if (x <= 0.08) {
        mausWartet = true;
        animiere(1, 160, uebernimm);
      }
    });
    buehne.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      haelt = false;
      if (naechste >= 0 && !lauf) animiere(0, 280, verwirf);
      plane();
    });
    const oeffne = (neuerTab) => {
      const i = gemeint();
      if (i < 0) return false;
      const url = figuren[i].dataset.url;
      if (neuerTab) window.open(url, '_blank', 'noopener');
      else window.location.assign(url);
      return true;
    };
    buehne.addEventListener('click', (e) => {
      if (zeiger !== 'mouse' || e.button !== 0) return;
      if (oeffne(e.ctrlKey || e.metaKey || e.shiftKey)) return;
      if (naechste < 0 || lauf) return;
      weg();
      mausWartet = true;
      animiere(1, 320, uebernimm);
    });
    // Middle button: no autoscroll over a picture that opens a product.
    buehne.addEventListener('mousedown', (e) => {
      if (e.button === 1 && gemeint() >= 0) e.preventDefault();
    });
    buehne.addEventListener('auxclick', (e) => {
      if (zeiger === 'mouse' && e.button === 1) oeffne(true);
    });

    // Touch and pen: drag sideways, release past a third to keep it.
    buehne.addEventListener('pointerdown', (e) => {
      zeiger = e.pointerType;
      if (e.pointerType === 'mouse' || lauf || n < 2) return;
      zug = { x: e.clientX, y: e.clientY, id: e.pointerId, aktiv: false };
    });
    buehne.addEventListener('pointermove', (e) => {
      if (!zug || e.pointerId !== zug.id) return;
      const dx = e.clientX - zug.x;
      const dy = e.clientY - zug.y;
      if (!zug.aktiv) {
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
        zug.aktiv = true;
        buehne.setPointerCapture(e.pointerId);
        bereite(dx < 0 ? akt + 1 : akt - 1, dx < 0 ? 1 : -1);
        buehne.classList.add('zeigt-kante');
        weg();
      }
      zeigeAnteil(klemme(Math.abs(dx) / buehne.clientWidth));
    });
    const loslassen = (e) => {
      if (!zug || e.pointerId !== zug.id) return;
      const war = zug.aktiv;
      zug = null;
      if (!war) return;
      if (anteil > 0.33) animiere(1, 260, uebernimm);
      else animiere(0, 260, verwirf);
      plane();
    };
    buehne.addEventListener('pointerup', loslassen);
    buehne.addEventListener('pointercancel', loslassen);

    zeigeAnteil(0);
    ordne();
  };

  /* ── 2 · Themen ────────────────────────────────────────────────── */
  // From 990 px every group is a panorama with one strip open. Positions are
  // fractions of the row, the same formula the section writes for the first
  // strip: left_i = i · (zu + luecke) + (i > aktiv ? offen − zu : 0). Below
  // 990 px the tiles are plain links and nothing here applies.
  const breit = window.matchMedia('(min-width: 990px)');
  module.themen = (root) => {
    root.querySelectorAll('[data-reihe]').forEach((reihe) => {
      const streifen = [...reihe.querySelectorAll('[data-streifen]')];
      let aktiv = 0;
      let uhr = null;
      const oeffne = (a) => {
        if (a === aktiv) return;
        aktiv = a;
        streifen.forEach((s, i) => {
          const nach = i > a ? ' + var(--offen) - var(--zu)' : '';
          s.style.setProperty('--l', `calc(${i} * (var(--zu) + var(--luecke))${nach})`);
          s.style.setProperty('--w', i === a ? 'var(--offen)' : 'var(--zu)');
          s.classList.toggle('ist-offen', i === a);
        });
      };
      streifen.forEach((s, i) => {
        s.addEventListener('pointerenter', (e) => {
          if (!breit.matches || e.pointerType !== 'mouse') return;
          clearTimeout(uhr);
          uhr = setTimeout(() => oeffne(i), 90);
        });
        s.addEventListener('pointerleave', () => clearTimeout(uhr));
        s.addEventListener('focus', () => breit.matches && oeffne(i));
        // Touch on a wide screen (tablet): the first tap opens, the second follows the link.
        s.addEventListener('click', (e) => {
          if (breit.matches && i !== aktiv) {
            e.preventDefault();
            oeffne(i);
          }
        });
      });
    });
  };

  /* ── 3 · Förderband ─────────────────────────────────────────────── */
  module.foerderband = (root) => {
    const bahn = root.querySelector('[data-bahn]');
    const liste = root.querySelector('[data-liste]');
    if (!bahn || !liste) return;

    // Every figure onto the floor: the photos are cut-outs with a transparent margin
    // below the feet, which would let them float above belt and carpet. Measured
    // from the alpha channel (umriss), applied as --sinken (a transform in CSS).
    // Needs a laid-out panel: again when a hidden tab is shown, and for the copies
    // of the endless belt.
    const aufDenBoden = () => {
      if (root.hidden) return;
      liste.querySelectorAll('img').forEach((img) => {
        figurenteile(img).then((m) => {
          const figur = img.closest('.mbb-band__figur');
          if (!m || !figur || !img.offsetHeight || !img.naturalWidth) return;
          // object-fit: contain + object-position bottom: the drawn photo is as tall
          // as the box allows at its own ratio, sits at the bottom of the box and is
          // centred in it (the box is centred in the figure's slot).
          const gezeichnet = Math.min(img.offsetHeight, (img.offsetWidth * img.naturalHeight) / img.naturalWidth);
          const breite = (gezeichnet * img.naturalWidth) / img.naturalHeight;
          const links = (figur.clientWidth - breite) / 2;
          // The MAIN figure's feet go onto the floor line; companions stand in front.
          const sinken = (1 - m.haupt.y1) * gezeichnet;
          img.style.setProperty('--sinken', `${sinken.toFixed(1)}px`);
          // Sets (a second figure in the photo): the shop photos frame both figures,
          // so the 400% one is drawn smaller than a single 400% figure — measured
          // 87.5–89.2 % of the photo height against 91.7 % (Betreiber 03.11.2026:
          // on belt and carpet the sizes did not match). Scaled up to the singles'
          // height around its own feet, at most 8 %; singles stay as they are.
          const hoehe = m.haupt.y1 - m.haupt.y0;
          const gross = m.neben.length && hoehe < GROESSE_EINZELFIGUR ? Math.min(1.08, GROESSE_EINZELFIGUR / hoehe) : 1;
          const fussX = (img.offsetWidth - breite) / 2 + ((m.haupt.x0 + m.haupt.x1) / 2) * breite;
          img.style.setProperty('--gross', gross.toFixed(3));
          img.style.setProperty('--ox', `${fussX.toFixed(1)}px`);
          img.style.setProperty('--oy', `${(img.offsetHeight - sinken).toFixed(1)}px`);
          // A shadow under each figure, at its own feet — scaled with the photo
          // around the main figure's feet.
          const mitteX = links + ((m.haupt.x0 + m.haupt.x1) / 2) * breite;
          const schatten = (t) => {
            const x = links + (t.x0 - (t.x1 - t.x0) * 0.08) * breite;
            return { x: mitteX + (x - mitteX) * gross, w: (t.x1 - t.x0) * 1.16 * breite * gross };
          };
          const s1 = schatten(m.haupt);
          figur.style.setProperty('--s1x', `${s1.x.toFixed(1)}px`);
          figur.style.setProperty('--s1w', `${s1.w.toFixed(1)}px`);
          const neben = m.neben.sort((a, z) => z.x1 - z.x0 - (a.x1 - a.x0))[0];
          if (neben) {
            const s2 = schatten(neben);
            figur.style.setProperty('--s2x', `${s2.x.toFixed(1)}px`);
            figur.style.setProperty('--s2w', `${s2.w.toFixed(1)}px`);
            figur.style.setProperty('--s2y', `${Math.max(0, (neben.y1 - m.haupt.y1) * gezeichnet * gross).toFixed(1)}px`);
          }
          // The arrival seal follows the head: top of the main figure in the box.
          const kopf = img.offsetHeight - gezeichnet + m.haupt.y0 * gezeichnet + sinken - (gross - 1) * hoehe * gezeichnet;
          figur.style.setProperty('--kopf', `${Math.max(0, kopf - 14).toFixed(1)}px`);
        });
      });
    };
    aufDenBoden();
    root.addEventListener('mbb-gezeigt', aufDenBoden);

    // The arrivals hall behind the belt: its image only once the belt is near.
    const halle = root.querySelector('[data-halle]');
    if (halle) {
      const io = beobachte(halle, (s) => {
        if (!s) return;
        halle.classList.add('ist-nah');
        io.disconnect();
      }, '600px');
    }

    // „seit Dienstag da“ — relative to today in the visitor's browser.
    const heute = new Date();
    heute.setHours(0, 0, 0, 0);
    const wochentage = EN
      ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      : ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    root.querySelectorAll('[data-seit]').forEach((el) => {
      const [j, m, t] = el.dataset.seit.split('-').map(Number);
      const tag = new Date(j, m - 1, t);
      const diff = Math.round((heute - tag) / 864e5);
      if (diff <= 0) el.textContent = EN ? 'arrived today' : 'heute eingetroffen';
      else if (diff === 1) el.textContent = EN ? 'here since yesterday' : 'seit gestern da';
      else if (diff < 7) el.textContent = EN ? `here since ${wochentage[tag.getDay()]}` : `seit ${wochentage[tag.getDay()]} da`;
    });

    // Mouse drag (touch scrolls natively). A drag must not open the product.
    let pos = bahn.scrollLeft;
    let zug = null;
    let gezogen = false;
    bahn.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      zug = { x: e.clientX, links: bahn.scrollLeft };
      gezogen = false;
    });
    window.addEventListener('pointermove', (e) => {
      if (!zug) return;
      const dx = e.clientX - zug.x;
      if (!gezogen && Math.abs(dx) > 5) {
        gezogen = true;
        bahn.classList.add('zieht');
      }
      if (gezogen) {
        bahn.scrollLeft = zug.links - dx;
        pos = bahn.scrollLeft;
      }
    });
    window.addEventListener('pointerup', () => {
      if (!zug) return;
      zug = null;
      bahn.classList.remove('zieht');
    });
    bahn.addEventListener(
      'click',
      (e) => {
        if (gezogen) {
          e.preventDefault();
          gezogen = false;
        }
      },
      true,
    );

    // Bestseller stage: no drift and no copies. The figures march in once each time
    // the tab is shown (only those in view, one after another, two small hops on the
    // way — transform only), then stand; one spot wanders over the visible figures.
    if (root.querySelector('[data-roter-teppich]')) {
      const stuecke = [...liste.children];
      const sichtbare = () => {
        const b = bahn.getBoundingClientRect();
        return stuecke.filter((li) => {
          const r = li.getBoundingClientRect();
          return r.right > b.left + 20 && r.left < b.right - 20;
        });
      };
      const beruehrung = window.matchMedia('(hover: none), (pointer: coarse)');
      let spot = null;

      // Arrows (mouse): three figures per click, hidden at either end. Also with
      // reduced motion, then without gliding.
      const pfeile = [...root.querySelectorAll('[data-band-pfeil]')];
      const pfeilStand = () => {
        const max = bahn.scrollWidth - bahn.clientWidth;
        pfeile.forEach((p) => p.toggleAttribute('data-aus', Number(p.dataset.bandPfeil) < 0 ? bahn.scrollLeft <= 2 : bahn.scrollLeft >= max - 2));
      };
      pfeile.forEach((p) =>
        p.addEventListener('click', () => {
          const schritt = stuecke[1] ? stuecke[1].offsetLeft - stuecke[0].offsetLeft : 220;
          bahn.scrollBy({ left: Number(p.dataset.bandPfeil) * schritt * 3, behavior: ruhig.matches ? 'auto' : 'smooth' });
        }),
      );
      // The spot follows the visitor's browsing as soon as the row has stopped.
      let nachRuhe = 0;
      bahn.addEventListener(
        'scroll',
        () => {
          pfeilStand();
          clearTimeout(nachRuhe);
          nachRuhe = setTimeout(() => spot && spot(), 250);
        },
        { passive: true },
      );

      // Swipe hint (touch screens): until the first touch, at most 7 s.
      const pille = root.querySelector('[data-wisch-hinweis]');
      let pillenZeit = 0;
      const pilleWeg = () => {
        clearTimeout(pillenZeit);
        if (pille) pille.classList.remove('ist-sichtbar');
      };
      const pilleZeigen = () => {
        if (!pille || !beruehrung.matches) return;
        pille.classList.add('ist-sichtbar');
        pillenZeit = setTimeout(pilleWeg, 7000);
      };
      bahn.addEventListener('touchstart', pilleWeg, { passive: true });

      // Cut-off figure at the right edge: the row visibly goes on. Only as far as
      // the left padding allows, so the first star is never cut.
      const anschneiden = () => {
        const b = bahn.getBoundingClientRect();
        const rand = b.right - (beruehrung.matches ? 0 : b.width * 0.04);
        const naechste = stuecke.find((li) => li.getBoundingClientRect().right > rand);
        if (!naechste) return;
        const r = naechste.getBoundingClientRect();
        const sichtbar = (rand - r.left) / r.width;
        if (sichtbar > 0.25 && sichtbar < 0.75) return;
        const frei = parseFloat(getComputedStyle(liste).paddingLeft) - 8;
        const verschieben = Math.min(r.left + r.width * 0.5 - rand, frei);
        if (verschieben > 0 && bahn.scrollLeft === 0) bahn.scrollLeft = verschieben;
      };

      if (ruhig.matches) {
        root.addEventListener('mbb-gezeigt', () => {
          pfeilStand();
          pilleZeigen();
        });
        return;
      }

      // The figures march in once each time the tab is shown (only those in view,
      // one after another, two small hops — transform only), then stand. On touch
      // screens the row then nudges once to the left and back, with the hint.
      root.addEventListener('mbb-gezeigt', () => {
        anschneiden();
        pfeilStand();
        const marsch = sichtbare();
        marsch.forEach((li, n) => {
          li.animate(
            [
              { transform: 'translateX(110vw)' },
              { transform: 'translate(55vw, -6px)', offset: 0.35 },
              { transform: 'translate(22vw, 0)', offset: 0.6 },
              { transform: 'translate(6vw, -4px)', offset: 0.82 },
              { transform: 'none' },
            ],
            { duration: 1100, delay: n * 110, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'backwards' },
          );
        });
        const ende = Math.max(0, marsch.length - 1) * 110 + 1100;
        if (beruehrung.matches) {
          setTimeout(() => {
            pilleZeigen();
            liste.animate(
              [{ transform: 'none' }, { transform: 'translateX(-56px)', offset: 0.45 }, { transform: 'none' }],
              { duration: 1300, easing: 'cubic-bezier(0.45, 0, 0.25, 1)' },
            );
          }, ende + 200);
        }
      });
      let aktiv = null;
      let takt = 0;
      spot = () => {
        if (document.hidden) return;
        const s = sichtbare();
        if (!s.length) return;
        const naechste = s[(s.indexOf(aktiv) + 1) % s.length];
        if (aktiv) aktiv.classList.remove('ist-im-licht');
        naechste.classList.add('ist-im-licht');
        aktiv = naechste;
      };
      beobachte(root, (s) => {
        clearInterval(takt);
        if (!s) return;
        spot();
        takt = setInterval(spot, 1800);
      });
      return;
    }

    if (ruhig.matches || liste.children.length < 4) return;

    // Endless belt: a copy follows the originals (hidden from screen readers and keyboard).
    const original = [...liste.children];
    original.forEach((li) => {
      const kopie = li.cloneNode(true);
      kopie.setAttribute('aria-hidden', 'true');
      kopie.querySelectorAll('a').forEach((a) => (a.tabIndex = -1));
      // The seals' SVG defs carry ids; the copy keeps using the original's (url(#…) is document-wide).
      kopie.querySelectorAll('[id]').forEach((e) => e.removeAttribute('id'));
      liste.appendChild(kopie);
    });
    aufDenBoden();
    const periode = () => liste.children[original.length].offsetLeft - liste.children[0].offsetLeft;
    let sichtbar = false;
    let pause = 0;
    let haelt = false;
    let letzte = 0;
    let wirSchreiben = false;
    const halte = (ms) => (pause = performance.now() + ms);

    bahn.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && (haelt = true));
    bahn.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && (haelt = false));
    bahn.addEventListener('focusin', () => (haelt = true));
    bahn.addEventListener('focusout', () => (haelt = false));
    bahn.addEventListener('touchstart', () => halte(4000), { passive: true });
    bahn.addEventListener('wheel', () => halte(2500), { passive: true });
    bahn.addEventListener(
      'scroll',
      () => {
        if (wirSchreiben) {
          wirSchreiben = false;
          return;
        }
        pos = bahn.scrollLeft;
        const p = periode();
        if (pos >= p) {
          pos -= p;
          wirSchreiben = true;
          bahn.scrollLeft = pos;
        }
      },
      { passive: true },
    );

    const tick = (t) => {
      const dt = letzte ? Math.min(64, t - letzte) : 0;
      letzte = t;
      if (sichtbar && !haelt && !zug && t > pause && !document.hidden) {
        pos += (dt / 1000) * 26;
        const p = periode();
        if (pos >= p) pos -= p;
        wirSchreiben = true;
        bahn.scrollLeft = pos;
      }
      if (sichtbar) requestAnimationFrame(tick);
      else letzte = 0;
    };
    beobachte(root, (s) => {
      const war = sichtbar;
      sichtbar = s;
      if (s && !war) requestAnimationFrame(tick);
    });
  };

  /* ── 3b · Reiter des Förderbands (Neu eingetroffen · Bestseller) ── */
  // Each panel is its own belt (data-mbb="foerderband"); a hidden panel does not
  // intersect, so its belt starts drifting only once it is shown, and its lazy
  // images load then. Keyboard as in the ARIA tabs pattern: arrows, Home, End.
  module.bandreiter = (root) => {
    const reiter = [...root.querySelectorAll('[data-band-reiter]')];
    const felder = [...root.querySelectorAll('[data-band-feld]')];
    if (reiter.length < 2 || reiter.length !== felder.length) return;
    const zeige = (n, fokus) => {
      reiter.forEach((r, i) => {
        const an = i === n;
        r.setAttribute('aria-selected', an ? 'true' : 'false');
        r.tabIndex = an ? 0 : -1;
        felder[i].hidden = !an;
      });
      // The panel's own module reacts (the stage marches its figures in).
      felder[n].dispatchEvent(new CustomEvent('mbb-gezeigt'));
      if (fokus) reiter[n].focus();
    };
    reiter.forEach((r, i) => {
      r.addEventListener('click', () => zeige(i, false));
      r.addEventListener('keydown', (e) => {
        const tasten = { ArrowRight: (i + 1) % reiter.length, ArrowLeft: (i - 1 + reiter.length) % reiter.length, Home: 0, End: reiter.length - 1 };
        if (!(e.key in tasten)) return;
        e.preventDefault();
        zeige(tasten[e.key], true);
      });
    });
  };

  /* ── 2b · Bewertungen (Mosaik) ──────────────────────────────────── */
  // Four reviews at a glance. „Andere Stimmen“ deals the next four with the
  // rule the section used for set 0: the tall tile takes the photo reviews one
  // after the other (without any, a review with its bear), the other three the
  // remaining reviews three at a time, the longest of them in the wide tile.
  // Every review exists once; dealing moves it between its tile and the pool.
  // A click on a tile opens the whole review in a dialog.
  module.bewertungen = (root) => {
    const plaetze = [...root.querySelectorAll('[data-platz]')];
    const vorrat = root.querySelector('[data-vorrat]');
    const kacheln = root.querySelector('[data-kacheln]');
    if (plaetze.length !== 4 || !vorrat || !kacheln) return;
    const alle = [...root.querySelectorAll('[data-stimme]')].sort((a, b) => a.dataset.nr - b.dataset.nr);
    const fotos = alle.filter((s) => s.dataset.art === 'foto');
    const texte = alle.filter((s) => s.dataset.art === 'text');
    const mitFoto = fotos.length > 0;
    // Only as many sets as can be filled without repeating a review (operator
    // 04.11.2026: the fifth set brought back the first photo and two quotes, which
    // irritates). One photo and three quotes per set — so the smaller of the two
    // decides; a review that does not fit a full set stays unshown. On a product page
    // the reviews of that product are left out, which can cost a set there.
    const saetze = Math.max(1, mitFoto ? Math.min(fotos.length, Math.floor(texte.length / 3)) : Math.floor(texte.length / 4));
    const knopf = root.querySelector('[data-mischen]');
    const zaehler = root.querySelector('[data-zaehler]');
    let satz = 0;

    // The bear stands on the tile edge at full box height (as wide as the box allows).
    const stelleBaer = (stimme) => {
      const box = stimme.querySelector('.mbb-bw__baer');
      const img = box && box.querySelector('img');
      if (!img || !box.offsetWidth) return;
      umriss(img).then((u) => {
        if (!u || !box.offsetWidth) return;
        const b = box.clientWidth;
        const h = box.clientHeight;
        stelle(img, u, { hoehe: Math.min(h, b / seitenverhaeltnis(img, u)), mitte: b / 2, boden: h });
      });
    };
    const richte = () =>
      plaetze.forEach((platz) => {
        const s = platz.querySelector('[data-stimme]');
        if (s) stelleBaer(s);
      });

    // Longest of three into the wide tile, ties to the earlier one (as in Liquid).
    const ordne = (drei) => {
      const z = drei.map((s) => Number(s.dataset.zeichen) || 0);
      const lang = z[0] >= z[1] && z[0] >= z[2] ? 0 : z[1] >= z[2] ? 1 : 2;
      const rest = drei.filter((_, i) => i !== lang);
      return [rest[0], rest[1], drei[lang]];
    };
    const inhalt = (n) => {
      if (mitFoto) return [fotos[n % fotos.length], ...ordne([0, 1, 2].map((j) => texte[(n * 3 + j) % texte.length]))];
      const vier = [0, 1, 2, 3].map((j) => texte[(n * 4 + j) % texte.length]);
      return [vier[0], ...ordne(vier.slice(1))];
    };

    const zeige = (n) => {
      const neu = inhalt(n);
      plaetze.forEach((platz, i) => {
        const tausche = () => {
          const alt = platz.querySelector('[data-stimme]');
          if (alt && alt !== neu[i]) vorrat.appendChild(alt);
          platz.appendChild(neu[i]);
          stelleBaer(neu[i]);
        };
        if (ruhig.matches) return tausche();
        // The tile turns away, the next review turns in.
        setTimeout(() => {
          platz.classList.add('dreht');
          setTimeout(() => {
            tausche();
            requestAnimationFrame(() => requestAnimationFrame(() => platz.classList.remove('dreht')));
          }, 300);
        }, i * 80);
      });
    };

    if (zaehler) zaehler.textContent = `1 / ${saetze}`;
    if (knopf && saetze < 2) knopf.closest('.mbb-bw__steuerung').hidden = true;
    knopf?.addEventListener('click', () => {
      satz = (satz + 1) % saetze;
      zeige(satz);
      if (zaehler) zaehler.textContent = `${satz + 1} / ${saetze}`;
      if (kacheln.scrollLeft) kacheln.scrollTo({ left: 0, behavior: ruhig.matches ? 'auto' : 'smooth' });
    });

    const dialog = root.querySelector('[data-bw-dialog]');
    const ziel = dialog && dialog.querySelector('[data-bw-inhalt]');
    if (dialog && ziel && dialog.showModal) {
      root.addEventListener('click', (e) => {
        const b = e.target.closest('[data-ganz]');
        if (!b) return;
        const innen = b.closest('[data-stimme]').querySelector('.mbb-bw__innen').cloneNode(true);
        innen.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
        ziel.replaceChildren(innen);
        dialog.showModal();
      });
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) dialog.close();
      });
    }

    new ResizeObserver(richte).observe(kacheln);
  };

  /* ── 5 · Blindbox ───────────────────────────────────────────────── */
  // The lid of the box photo (section setting `deckel`, % of the square
  // photo; drawn in an SVG of 1000 × 1000). The photo is taken from above a
  // corner, nearly orthographic: a point of the lid turned by θ about its
  // hinge lies at hinge + cos θ · v + sin θ · u (v = hinge → free edge,
  // u = the lid standing upright). That is an affine map of the photo, so
  // the lid is a copy of the photo clipped to the opening and transformed.
  // Past edge-on the map mirrors (det < 0): from there the inside of the lid
  // is drawn as a plain cardboard face instead. The opening shows the two far
  // walls of the box, which meet below the back corner.
  const vieleck = (liste) => liste.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  function deckelVon(svg) {
    let g;
    try {
      g = JSON.parse(svg.dataset.deckel || '');
    } catch (e) {
      return null;
    }
    const pt = (p) => (Array.isArray(p) ? { x: p[0] * 10, y: p[1] * 10 } : null);
    const hinten = pt(g.hinten);
    const links = pt(g.links);
    const vorn = pt(g.vorn);
    const rechts = pt(g.rechts);
    const u = pt(g.hoch);
    if (!hinten || !links || !vorn || !rechts || !u) return null;
    const plus = (...v) => v.reduce((s, a) => ({ x: s.x + a.x, y: s.y + a.y }), { x: 0, y: 0 });
    const mal = (a, k) => ({ x: a.x * k, y: a.y * k });
    const minus = (a, b) => ({ x: a.x - b.x, y: a.y - b.y });
    // Hinge from the back corner to the left or right corner; the free edge
    // is the opposite one (the edge with the tuck flap).
    const amLinks = g.scharnier !== 'rechts';
    const A = hinten;
    const B = amLinks ? links : rechts;
    const h = minus(B, A);
    const v = mal(plus(minus(vorn, B), minus(amLinks ? rechts : links, A)), 0.5);
    const det0 = h.x * v.y - h.y * v.x;
    if (Math.abs(det0) < 1) return null;
    const umriss = Array.isArray(g.oeffnung) && g.oeffnung.length > 2 ? g.oeffnung.map(pt) : [hinten, rechts, vorn, links];
    svg.querySelectorAll('[data-oeffnung-form]').forEach((p) => p.setAttribute('points', vieleck(umriss)));
    // Far walls: from just above the rim (the hinge edge bulges a little) far down; the opening clips them.
    const rand = mal(u, 0.15);
    const tief = mal(u, -3);
    const wand = (a, b) => vieleck([plus(a, rand), plus(b, rand), plus(b, tief), plus(a, tief)]);
    svg.querySelector('[data-wand="links"]').setAttribute('points', wand(links, hinten));
    svg.querySelector('[data-wand="rechts"]').setAttribute('points', wand(hinten, rechts));
    const mitte = mal(plus(hinten, links, vorn, rechts), 0.25);
    const glut = svg.querySelector('[data-glut]');
    glut.setAttribute('cx', mitte.x.toFixed(1));
    glut.setAttribute('cy', (mitte.y + 20).toFixed(1));
    glut.setAttribute('rx', (Math.abs(rechts.x - links.x) * 0.42).toFixed(1));
    glut.setAttribute('ry', (Math.abs(vorn.y - hinten.y) * 0.62).toFixed(1));
    const gruppe = svg.querySelector('[data-deckel-gruppe]');
    const innen = svg.querySelector('[data-deckel-innen]');
    const zeichne = (theta) => {
      const c = Math.cos(theta);
      const s = Math.sin(theta);
      const w = { x: c * v.x + s * u.x, y: c * v.y + s * u.y };
      const flaeche = (h.x * w.y - h.y * w.x) / det0;
      if (flaeche > 0.02) {
        const a = (h.x * v.y - w.x * h.y) / det0;
        const cc = (h.x * (w.x - v.x)) / det0;
        const b = (h.y * (v.y - w.y)) / det0;
        const d = (h.x * w.y - h.y * v.x) / det0;
        const e = A.x - (a * A.x + cc * A.y);
        const f = A.y - (b * A.x + d * A.y);
        gruppe.setAttribute('transform', `matrix(${a.toFixed(5)} ${b.toFixed(5)} ${cc.toFixed(5)} ${d.toFixed(5)} ${e.toFixed(2)} ${f.toFixed(2)})`);
        gruppe.style.visibility = '';
        innen.style.visibility = 'hidden';
      } else {
        gruppe.style.visibility = 'hidden';
        innen.setAttribute('points', vieleck([A, B, plus(B, w), plus(A, w)]));
        // Darker while nearly edge-on, lighter when it faces the visitor.
        innen.style.fill = `hsl(36 14% ${(64 + 18 * Math.min(1, -flaeche * 1.3)).toFixed(1)}%)`;
        innen.style.visibility = '';
      }
    };
    zeichne(0);
    return { zeichne, mitte };
  }

  // A small burst out of the opened box: flash, a ring, puffs, confetti and
  // sparks — spans animated with the Web Animations API (transform/opacity),
  // removed when they are done. Distances scale with the box.
  const funkenFarben = ['#d9ad4f', '#f1cf72', '#e3a6b6', '#ffffff', '#b9a3cc', '#9a8a66', '#d9ad4f', '#ffffff'];
  function knall(ebene, x, y, s) {
    const teile = [];
    const zufall = (a, b) => a + Math.random() * (b - a);
    const teil = (klasse, w, hoehe, farbe) => {
      const el = document.createElement('span');
      el.className = `mbb-funke ${klasse}`;
      el.style.width = `${w}px`;
      el.style.height = `${hoehe}px`;
      el.style.marginLeft = `${-w / 2}px`;
      el.style.marginTop = `${-hoehe / 2}px`;
      if (farbe) el.style.background = farbe;
      teile.push(el);
      return el;
    };
    const bei = (dx, dy, rest = '') => `translate(${(x + dx).toFixed(1)}px, ${(y + dy).toFixed(1)}px) ${rest}`;
    const laeufe = [];
    const lauf = (el, frames, dauer, extra = {}) => laeufe.push(() => el.animate(frames, { duration: dauer, fill: 'forwards', ...extra }));

    const blitz = teil('mbb-funke--blitz', s * 0.95, s * 0.95);
    lauf(blitz, [
      { transform: bei(0, 0, 'scale(0.15)'), opacity: 0 },
      { transform: bei(0, -s * 0.04, 'scale(0.85)'), opacity: 1, offset: 0.16 },
      { transform: bei(0, -s * 0.08, 'scale(1.45)'), opacity: 0 },
    ], 700, { easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
    const welle = teil('mbb-funke--welle', s * 0.42, s * 0.42);
    lauf(welle, [
      { transform: bei(0, 0, 'scale(0.1)'), opacity: 0.95 },
      { transform: bei(0, -s * 0.05, 'scale(2.3)'), opacity: 0 },
    ], 720, { easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' });
    for (let i = 0; i < 5; i++) {
      const winkel = -Math.PI / 2 + zufall(-1.1, 1.1);
      const weit = s * zufall(0.14, 0.28);
      const puff = teil('mbb-funke--puff', s * 0.24, s * 0.24);
      lauf(puff, [
        { transform: bei(0, 0, 'scale(0.3)'), opacity: 0 },
        { transform: bei(Math.cos(winkel) * weit * 0.5, Math.sin(winkel) * weit * 0.5, 'scale(1)'), opacity: 0.85, offset: 0.25 },
        { transform: bei(Math.cos(winkel) * weit, Math.sin(winkel) * weit, 'scale(1.7)'), opacity: 0 },
      ], zufall(800, 1050), { easing: 'ease-out', delay: zufall(0, 80) });
    }
    // Confetti, dots and stars on a parabola with air drag: x = v·f(t), y = v·f(t) + g·t².
    const ziehen = (t) => (1 - Math.exp(-3 * t)) / (1 - Math.exp(-3));
    for (let i = 0; i < 30; i++) {
      const art = i % 10 < 4 ? 'konfetti' : i % 10 < 7 ? 'punkt' : 'stern';
      const farbe = funkenFarben[i % funkenFarben.length];
      const el =
        art === 'konfetti'
          ? teil('mbb-funke--konfetti', s * 0.022, s * 0.042, farbe)
          : art === 'punkt'
            ? teil('mbb-funke--punkt', s * 0.022, s * 0.022, farbe)
            : teil('mbb-funke--stern', s * 0.05, s * 0.05, farbe);
      const winkel = -Math.PI / 2 + zufall(-1.25, 1.25);
      const tempo = s * zufall(0.32, 0.78);
      const vx = Math.cos(winkel) * tempo;
      const vy = Math.sin(winkel) * tempo;
      const schwere = s * 0.75;
      const drehung = zufall(-540, 540);
      const frames = [];
      for (let k = 0; k <= 6; k++) {
        const t = k / 6;
        const f = ziehen(t);
        frames.push({
          transform: bei(vx * f, vy * f + schwere * t * t, `rotate(${(drehung * t).toFixed(0)}deg) scale(${k === 0 ? 0.2 : (1 - 0.35 * t).toFixed(2)})`),
          opacity: t < 0.62 ? 1 : Math.max(0, 1 - (t - 0.62) / 0.38),
        });
      }
      lauf(el, frames, zufall(950, 1350), { delay: zufall(0, 90) });
    }
    // Sparks: short streaks along their direction.
    for (let i = 0; i < 10; i++) {
      const winkel = -Math.PI / 2 + zufall(-1.3, 1.3);
      const weit = s * zufall(0.3, 0.5);
      const el = teil('mbb-funke--strahl', Math.max(2, s * 0.008), s * 0.07);
      const grad = (winkel * 180) / Math.PI + 90;
      lauf(el, [
        { transform: bei(0, 0, `rotate(${grad.toFixed(0)}deg) scaleY(0.2)`), opacity: 1 },
        { transform: bei(Math.cos(winkel) * weit * 0.6, Math.sin(winkel) * weit * 0.6, `rotate(${grad.toFixed(0)}deg) scaleY(1)`), opacity: 1, offset: 0.45 },
        { transform: bei(Math.cos(winkel) * weit, Math.sin(winkel) * weit, `rotate(${grad.toFixed(0)}deg) scaleY(0.2)`), opacity: 0 },
      ], zufall(420, 560), { easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
    }
    ebene.append(...teile);
    const animationen = laeufe.map((start) => start());
    Promise.all(animationen.map((a) => a.finished.catch(() => {}))).then(() => teile.forEach((el) => el.remove()));
  }

  // Sounds of the blindbox, synthesised with the Web Audio API: no audio files,
  // nothing loaded before the first click. Chosen by ear (Betreiber, 02.11.2026):
  // the box knocks while it wobbles, the burst goes „plopp“ with crackling
  // confetti, little bells run upwards with it, the foil rips; a secret adds a
  // glittering run after its word and a low bell under the figure. Every sound is
  // scheduled at an absolute time of the audio context.
  //
  // Opening the audio channel is slow, and the first knock is due 97 ms after the
  // click (Betreiber 02.11.2026: „das erste Schütteln funktioniert nicht“). Measured
  // in Chrome on a Windows laptop: creating the context in the click blocks the page
  // 44–680 ms; created ahead it costs one long task of ~135 ms, then resume() in the
  // click costs 0.2 ms — but the sound only runs 165–350 ms later. So: the channel
  // is opened when the section comes near (vorbereiten), a mouse over the box starts
  // it if the page has had a click already (wecken; also when the box comes into
  // view and on the mouse press), and the very first wobble waits for it, at most
  // 0.5 s (laeuft). Without a prepared channel the click creates it
  // after the first frame (Safari: in the click itself, mixing with other audio and
  // keeping to the silent switch); sounds asked for meanwhile wait, and what has
  // already passed stays silent.
  const klang = (() => {
    const AC = window.AudioContext || window.webkitAudioContext;
    let an = true;
    try {
      an = localStorage.getItem('mbb-ton') !== 'aus';
    } catch (e) {}
    let ctx = null;
    let ziel = null;
    let rauschen = null;
    let hall = null;
    let schlaf = 0;
    let wartend = null;
    // Resolves once the context runs (from the last resume or creation).
    let anlauf = null;
    const zufall = (a, b) => a + Math.random() * (b - a);
    const leerlauf = window.requestIdleCallback || ((f) => setTimeout(f, 200));
    const schlafen = () => {
      // An idle context is put to sleep; the next sound wakes it.
      clearTimeout(schlaf);
      schlaf = setTimeout(() => {
        if (ctx && ctx.state === 'running') ctx.suspend();
      }, 8000);
    };
    // The channel: context, volume, a gentle compressor so overlapping sounds never clip.
    const kanal = () => {
      try {
        if (navigator.audioSession) navigator.audioSession.type = 'ambient';
        ctx = new AC();
      } catch (e) {
        ctx = null;
        return false;
      }
      const komp = ctx.createDynamicsCompressor();
      komp.threshold.value = -10;
      komp.knee.value = 6;
      komp.ratio.value = 4;
      komp.attack.value = 0.003;
      komp.release.value = 0.15;
      ziel = ctx.createGain();
      ziel.gain.value = 0.75;
      ziel.connect(komp).connect(ctx.destination);
      return true;
    };
    // The material: one second of noise, and a short room for the bells — noise
    // decaying to −60 dB in 2.2 s.
    const material = () => {
      if (rauschen || !ctx) return;
      const sr = ctx.sampleRate;
      rauschen = ctx.createBuffer(1, sr, sr);
      const d = rauschen.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      const n = Math.floor(sr * 2.2);
      const impuls = ctx.createBuffer(2, n, sr);
      const faktor = Math.exp(Math.log(0.001) / n);
      for (let k = 0; k < 2; k++) {
        const e = impuls.getChannelData(k);
        let h = 1;
        for (let i = 0; i < n; i++, h *= faktor) e[i] = (Math.random() * 2 - 1) * h;
      }
      hall = ctx.createConvolver();
      hall.buffer = impuls;
      const hallPegel = ctx.createGain();
      hallPegel.gain.value = 0.32;
      hall.connect(hallPegel).connect(ziel);
    };
    const starte = () => {
      if (ctx.state !== 'running') anlauf = ctx.resume().catch(() => {});
      schlafen();
    };
    // Ahead of the first click, when the section comes near: before any user
    // action the context stays suspended (it runs at once if the page has had a
    // click — then it is put to sleep until needed).
    const vorbereiten = () => {
      if (!an || !AC || ctx || wartend) return;
      leerlauf(
        () => {
          if (!an || ctx || wartend || !kanal()) return;
          if (ctx.state === 'running') ctx.suspend();
          leerlauf(material, { timeout: 2000 });
        },
        { timeout: 2000 },
      );
    };
    // A mouse over the box: start the channel now, if the page has had a click
    // (sticky activation) — then the first click needs no wait.
    const wecken = () => {
      if (!an || !ctx || ctx.state === 'running') return;
      if (navigator.userActivation && navigator.userActivation.hasBeenActive) starte();
    };
    // Without a prepared channel: created by the click.
    const erzeuge = () => {
      const liste = wartend || [];
      wartend = null;
      if (!kanal()) return undefined;
      material();
      starte();
      const jetzt = performance.now();
      liste.forEach(([name, soll, rest]) => {
        const spaet = (jetzt - soll) / 1000;
        if (spaet < 1.5) sounds[name](ctx.currentTime + 0.01 - spaet, ...rest);
      });
      return anlauf;
    };
    // Called in a click: wakes the context or has it created.
    const bereit = () => {
      if (!an || !AC) return false;
      if (ctx) {
        starte();
        return true;
      }
      if (!wartend) {
        wartend = [];
        if (navigator.audioSession) erzeuge();
        else anlauf = new Promise((fertig) => requestAnimationFrame(() => setTimeout(() => fertig(erzeuge()), 0)));
      }
      return Boolean(ctx);
    };
    // Resolves once a sound starts on time: at once when the channel runs or there
    // is no sound, at the latest after max ms.
    const laeuft = (max) => {
      if (!an || !AC || !anlauf || (ctx && ctx.state === 'running')) return Promise.resolve();
      return Promise.race([anlauf, new Promise((fertig) => setTimeout(fertig, max))]);
    };

    const huelle = (g, t, spitze, anstieg, abfall) => {
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(Math.max(0.0002, spitze), t + anstieg);
      g.gain.exponentialRampToValueAtTime(0.0001, t + anstieg + abfall);
    };
    const ausgang = (pan, sende) => {
      const p = ctx.createStereoPanner ? ctx.createStereoPanner() : ctx.createGain();
      if (p.pan) p.pan.value = pan;
      p.connect(ziel);
      if (sende) {
        const s = ctx.createGain();
        s.gain.value = sende;
        p.connect(s).connect(hall);
      }
      return p;
    };
    // A sound that waited for the context may begin in the past: what has passed
    // stays silent (the API takes no time before 0), the rest plays on time.
    const rausch = (t, typ, f, q, spitze, anstieg, abfall, pan = 0, sende = 0) => {
      const filter = ctx.createBiquadFilter();
      if (t < ctx.currentTime) return filter;
      const quelle = ctx.createBufferSource();
      quelle.buffer = rauschen;
      quelle.loop = true;
      filter.type = typ;
      filter.frequency.value = f;
      filter.Q.value = q;
      const g = ctx.createGain();
      huelle(g, t, spitze, anstieg, abfall);
      quelle.connect(filter).connect(g).connect(ausgang(pan, sende));
      quelle.start(t, Math.random() * 0.9);
      quelle.stop(t + anstieg + abfall + 0.05);
      return filter;
    };
    const ton = (t, typ, f0, f1, gleit, spitze, anstieg, abfall, pan = 0, sende = 0) => {
      if (t < ctx.currentTime) return;
      const o = ctx.createOscillator();
      o.type = typ;
      o.frequency.setValueAtTime(f0, t);
      if (f1) o.frequency.exponentialRampToValueAtTime(f1, t + gleit);
      const g = ctx.createGain();
      huelle(g, t, spitze, anstieg, abfall);
      o.connect(g).connect(ausgang(pan, sende));
      o.start(t);
      o.stop(t + anstieg + abfall + 0.05);
    };
    const glocke = (t, f, v, pan, abfall = 1.3) => {
      ton(t, 'sine', f, 0, 0, 0.13 * v, 0.0015, abfall, pan, 0.45);
      ton(t, 'sine', f * 2, 0, 0, 0.035 * v, 0.0015, abfall * 0.5, pan, 0.45);
      ton(t, 'sine', f * 4.07, 0, 0, 0.012 * v, 0.001, abfall * 0.2, pan, 0.45);
    };

    const sounds = {
      // Cardboard knocks, the figure inside knocks back — at the turning points of
      // mbb-wackeln (15 / 35 / 55 / 75 % of 650 ms).
      ruetteln(t) {
        [[0.097, 0.75], [0.227, 0.64], [0.357, 0.47], [0.487, 0.3]].forEach(([d, s]) => {
          rausch(t + d, 'bandpass', zufall(1000, 1500), 1.6, 0.55 * s, 0.002, 0.05, zufall(-0.15, 0.15));
          ton(t + d, 'sine', 150, 80, 0.04, 0.4 * s, 0.002, 0.07);
          rausch(t + d + zufall(0.02, 0.035), 'bandpass', zufall(1800, 2600), 2.2, 0.22 * s, 0.002, 0.03, zufall(-0.2, 0.2));
        });
      },
      // A cork-like pop, a low thump, air rushing out, then confetti ticking down.
      knall(t) {
        ton(t, 'sine', 560, 130, 0.07, 0.75, 0.002, 0.16);
        ton(t, 'triangle', 1100, 300, 0.05, 0.12, 0.001, 0.06);
        rausch(t, 'highpass', 1800, 0.7, 0.5, 0.001, 0.018);
        ton(t, 'sine', 95, 42, 0.2, 0.55, 0.004, 0.25);
        const luft = rausch(t + 0.005, 'bandpass', 500, 0.8, 0.3, 0.03, 0.55, 0, 0.15);
        const z = (x) => Math.max(0, x);
        luft.frequency.setValueAtTime(500, z(t));
        luft.frequency.exponentialRampToValueAtTime(2800, z(t + 0.12));
        luft.frequency.exponentialRampToValueAtTime(800, z(t + 0.6));
        for (let i = 0; i < 26; i++) {
          rausch(t + 0.04 + 0.85 * Math.pow(Math.random(), 1.8), 'bandpass', zufall(3500, 7500), 2.5, zufall(0.06, 0.2), 0.001, zufall(0.006, 0.016), zufall(-0.7, 0.7), 0.2);
        }
      },
      // Little bells running up a major pentatonic, landing on two notes.
      gloeckchen(t, reich) {
        const folge = [783.99, 880, 1046.5, 1174.66, 1318.51, 1567.98, 1760, 2093];
        if (reich) folge.push(2349.32, 2637.02);
        folge.forEach((f, i) => glocke(t + i * 0.055, f, 0.7 + (0.3 * i) / folge.length, -0.5 + i / folge.length));
        const ende = t + folge.length * 0.055 + 0.05;
        glocke(ende, folge[folge.length - 1], 0.9, 0.2, 2.2);
        glocke(ende, folge[folge.length - 3], 0.6, -0.2, 2.2);
      },
      // A secret: a fast glittering run follows its word out of the box …
      secretFlug(t) {
        [2093, 2349.32, 2637.02, 3135.96, 3520, 4186.01, 4698.63, 5274.04, 6271.93].forEach((f, i) => glocke(t + i * 0.035, f, 0.45, -0.6 + i * 0.15, 0.5));
      },
      // … and a low bell sounds under the figure.
      secretGlocke(t) {
        [[1, 1, 3.2], [2, 0.5, 2.2], [2.756, 0.35, 1.6], [5.404, 0.12, 0.7]].forEach(([r, a, abfall]) => ton(t, 'sine', 523.25 * r, 0, 0, 0.16 * a, 0.003, abfall, 0, 0.6));
        ton(t, 'sine', 784.88, 0, 0, 0.07, 0.004, 2.4, 0, 0.6);
      },
      // The foil rips upwards, crackling, then falls away rustling.
      riss(t) {
        const band = rausch(t, 'bandpass', 2200, 0.9, 0.25, 0.015, 0.2, 0.1);
        band.frequency.setValueAtTime(2200, Math.max(0, t));
        band.frequency.exponentialRampToValueAtTime(4800, Math.max(0, t + 0.18));
        for (let i = 0; i < 30; i++) rausch(t + 0.17 * Math.pow(Math.random(), 0.8), 'bandpass', zufall(1800, 5500), 1.4, zufall(0.09, 0.4), 0.0008, zufall(0.004, 0.012), zufall(-0.3, 0.4));
        for (let i = 0; i < 10; i++) rausch(t + 0.25 + 0.35 * Math.random(), 'bandpass', zufall(2500, 6000), 1.8, zufall(0.035, 0.1), 0.001, zufall(0.005, 0.015), zufall(-0.3, 0.3));
      },
      // Confirms „sound on“.
      ping(t) {
        glocke(t, 1567.98, 0.8, 0, 0.9);
      },
    };
    const spiele = (name, versatz = 0, ...rest) => {
      if (!bereit()) {
        if (wartend) wartend.push([name, performance.now() + versatz * 1000, rest]);
        return;
      }
      material();
      sounds[name](ctx.currentTime + 0.01 + versatz, ...rest);
    };
    return {
      moeglich: Boolean(AC),
      get an() {
        return an;
      },
      schalte(wert) {
        an = wert;
        try {
          if (wert) localStorage.removeItem('mbb-ton');
          else localStorage.setItem('mbb-ton', 'aus');
        } catch (e) {}
        // Off stops what is still ringing, and what still waits for the context.
        if (!wert && wartend) wartend.length = 0;
        if (!wert && ctx && ctx.state === 'running') ctx.suspend();
        // Every sound switch on the page shows the same state.
        document.dispatchEvent(new CustomEvent('mbb-ton', { detail: { an: wert } }));
      },
      // Other sections add their sounds here: fn(t, werkzeug, ...rest), scheduled
      // like the blindbox sounds and silent while the sound is off.
      neu(name, fn) {
        sounds[name] = (t, ...rest) => fn(t, { ton, rausch, glocke, zufall }, ...rest);
      },
      bereit,
      vorbereiten,
      wecken,
      laeuft,
      spiele,
    };
  })();

  module.blindbox = (root) => {
    const quelle = root.querySelector('[data-figuren]');
    let daten = [];
    try {
      daten = JSON.parse(quelle ? quelle.textContent : '[]');
    } catch (e) {
      daten = [];
    }
    const knopf = root.querySelector('[data-oeffnen]');
    if (!knopf || daten.length === 0) return;
    const serieName = root.dataset.serieName || 'Series';
    const linie = root.querySelector('.mbb-box__linie');
    const buehne = root.querySelector('[data-buehne]');
    const dose = root.querySelector('[data-dose]');
    const boxBild = dose && dose.querySelector('img');
    const deckelSvg = dose && dose.querySelector('[data-deckel]');
    const deckelBild = deckelSvg && deckelSvg.querySelector('[data-deckel-bild]');
    const deckel = deckelSvg ? deckelVon(deckelSvg) : null;
    const funken = root.querySelector('[data-funken]');
    const figur = root.querySelector('[data-figur]');
    const figurBild = root.querySelector('[data-figur-bild]');
    const figurLink = root.querySelector('[data-figur-link]');
    const karte = root.querySelector('[data-begleitkarte]');
    const karteBild = root.querySelector('[data-begleitkarte-bild]');
    const tuete = root.querySelector('[data-tuete]');
    const tueteBilder = [...root.querySelectorAll('[data-tuete-bild]')];
    const ergebnis = root.querySelector('[data-ergebnis]');
    const nameEl = root.querySelector('[data-name]');
    const quoteEl = root.querySelector('[data-quote]');
    const secretEl = root.querySelector('[data-secret]');
    const link = root.querySelector('[data-link]');
    const linkText = root.querySelector('[data-link-text]') || link;
    const intro = root.querySelector('[data-intro]');
    const nochmal = root.querySelector('[data-nochmal]');
    const kiLabel = root.querySelector('[data-ki]');
    const secretWort = root.querySelector('[data-secret-wort]');
    const sammlung = root.querySelector('[data-sammlung]');
    const tonKnopf = root.querySelector('[data-ton]');
    // The result takes the place of the intro (same grid cell) and gives it back on the next box.
    const leer = () => {
      ergebnis.setAttribute('data-leer', '');
      if (intro) intro.removeAttribute('data-aus');
    };
    const sammlungText = root.querySelector('[data-sammlung-text]');
    const fortschritt = root.querySelector('[data-fortschritt]');
    const slots = root.querySelector('[data-slots]');
    const speicher = `mbb-blindbox-${root.dataset.serie || 'serie'}`;

    // The lid is a copy of the box photo: same file as the <img>, set as soon
    // as that one has loaded, so it is decoded long before the first tap.
    const deckelQuelle = () => {
      if (deckelBild && boxBild && boxBild.currentSrc && deckelBild.getAttribute('href') !== boxBild.currentSrc) {
        deckelBild.setAttribute('href', boxBild.currentSrc);
      }
    };
    if (boxBild) {
      if (boxBild.complete) deckelQuelle();
      else boxBild.addEventListener('load', deckelQuelle, { once: true });
    }

    // The line-up keeps the order of the Series collection in the shop (Medicom Toy's order,
    // secrets beside their theme) — the Liquid loop already delivers it that way, never re-sort.
    let gefunden = new Set();
    try {
      gefunden = new Set(JSON.parse(localStorage.getItem(speicher) || '[]'));
    } catch (e) {
      gefunden = new Set();
    }
    const merke = () => {
      try {
        localStorage.setItem(speicher, JSON.stringify([...gefunden]));
      } catch (e) {
        /* private mode: the collection lives only in this page view */
      }
    };

    // Draw by Medicom Toy's mixing ratio. The box prints it for the regular
    // figures only; the boxes those rates leave over hold the figures without
    // one (the secrets) — Series 52: 80,6 % regular, so 19,4 % for seven
    // secrets, shared equally because nothing tells them apart. The page states
    // no odds for them. Before, each secret got a quarter of the rarest regular
    // rate (0,9 % each, 6,3 % together) and hardly ever came.
    const quoten = daten.filter((d) => d.q > 0).map((d) => d.q);
    const ohneQuote = daten.length - quoten.length;
    const rest = 1 - quoten.reduce((a, q) => a + q, 0);
    const selten = quoten.length ? Math.min(...quoten) : 1;
    const anteilOhneQuote = ohneQuote > 0 && rest > 0.01 ? rest / ohneQuote : selten / 4;
    const gewicht = (d) => (d.q > 0 ? d.q : anteilOhneQuote);
    const summe = daten.reduce((a, d) => a + gewicht(d), 0);
    const ziehe = () => {
      let r = Math.random() * summe;
      for (const d of daten) {
        r -= gewicht(d);
        if (r <= 0) return d;
      }
      return daten[daten.length - 1];
    };
    // The odds are told per case of 24 boxes, never as „1 in 7“ (Betreiber,
    // 02.11.2026). Medicom's mixing ratio is a whole count per few cases; the shop
    // keeps it as a percentage cut to its last digit (11/192 = 5,729… % is stored
    // as 0.0572, 1/24 as 0.0416). So: the smallest number of cases whose count
    // gives back exactly the stored value at its precision — truncated or rounded.
    // 0.1458 → 7 of 48, 0.0572 → 11 of 192, 0.0729 → 7 of 96, 0.0312 → 3 of 96.
    // Nothing fits within 20 cases (a hand-typed value): the closest count.
    const quote = (q) => {
      const stellen = (String(q).split('.')[1] || '').length;
      const schritt = 10 ** -stellen;
      let beste = null;
      for (let kartons = 1; kartons <= 20; kartons++) {
        const boxen = 24 * kartons;
        const stueck = Math.max(1, Math.round(q * boxen));
        const anteil = stueck / boxen;
        if (anteil >= q - schritt / 2 - 1e-12 && anteil < q + schritt - 1e-12) {
          beste = { stueck, boxen };
          break;
        }
        const abweichung = Math.abs(anteil - q);
        if (kartons <= 10 && (!beste || abweichung < beste.abweichung)) beste = { stueck, boxen, abweichung };
      }
      if (EN) return `About ${beste.stueck} in ${beste.boxen} boxes of the ${serieName} ${beste.stueck === 1 ? 'contains' : 'contain'} this figure.`;
      const verb = beste.stueck === 1 ? 'enthält' : 'enthalten';
      return `Etwa ${beste.stueck} von ${beste.boxen} Boxen der ${serieName} ${verb} diese Figur.`;
    };

    // „Leeren“ keeps the old collection for a few seconds, during which the same
    // button reads „Rückgängig“. A new box ends that window: it starts the new one.
    const leeren = root.querySelector('[data-leeren]');
    let rueckgabe = null;
    const vergissRueckgabe = () => {
      if (!rueckgabe) return;
      clearTimeout(rueckgabe.uhr);
      rueckgabe = null;
    };
    const zeigeLeeren = () => {
      if (!leeren) return;
      const aus = !rueckgabe && !daten.some((d) => gefunden.has(d.h));
      if (aus && document.activeElement === leeren) {
        // Hidden while focused would drop the focus on <body>: hand it to the box or „Noch eine Box“.
        (ergebnis.hasAttribute('data-leer') ? knopf : nochmal).focus({ preventScroll: true });
      }
      leeren.toggleAttribute('data-aus', aus);
      leeren.toggleAttribute('data-zurueck', !!rueckgabe);
      leeren.setAttribute('aria-label', EN ? (rueckgabe ? 'Undo clearing' : 'Clear the collection') : rueckgabe ? 'Leeren rückgängig machen' : 'Sammlung leeren');
    };

    const zeigeSammlung = (neu) => {
      if (!sammlung) return;
      const gesamt = daten.length;
      const zahl = daten.filter((d) => gefunden.has(d.h)).length;
      sammlungText.textContent = EN ? `${zahl} of ${gesamt} found` : `${zahl} von ${gesamt} entdeckt`;
      if (fortschritt) fortschritt.style.setProperty('--mbb-anteil', String(zahl / gesamt));
      zeigeLeeren();
      slots.classList.remove('ist-geleert');
      // Picture field plus a theme line under it; the line is always there (empty
      // until found), so a find never changes the height of the grid.
      slots.replaceChildren(
        ...daten.map((d) => {
          const li = document.createElement('li');
          li.className = 'mbb-box__slot';
          const feld = document.createElement('span');
          feld.className = 'mbb-box__slot-bild';
          const thema = document.createElement('span');
          thema.className = 'mbb-box__slot-thema';
          // Non-breaking spaces: a plain space would leave the line without height.
          thema.textContent = '\u00a0';
          const geheim = document.createElement('span');
          geheim.className = 'mbb-box__slot-geheim';
          geheim.textContent = '\u00a0';
          if (gefunden.has(d.h)) {
            li.classList.add('ist-gefunden');
            if (d.h === neu) li.classList.add('ist-neu');
            li.title = d.t ? `${d.t}: ${d.n}` : d.n;
            // A secret shows its AI image here as on the stage, marked „AI“ (note beside the counter).
            if (d.k) {
              const img = document.createElement('img');
              img.src = d.k;
              img.alt = `${serieName} ${d.n}${d.i ? (EN ? ' (AI-generated image)' : ' (mit KI erzeugtes Bild)') : ''}`;
              img.loading = 'lazy';
              feld.appendChild(img);
              if (d.i) {
                const marke = document.createElement('span');
                marke.className = 'mbb-box__ki-marke mbb-box__ki-marke--feld';
                marke.textContent = 'AI';
                marke.setAttribute('aria-hidden', 'true');
                feld.appendChild(marke);
              }
            } else {
              feld.textContent = '✓';
            }
            if (d.t) thema.textContent = d.t;
            if (d.s) {
              geheim.textContent = 'Secret';
              geheim.classList.add('ist-secret');
            }
          } else {
            feld.textContent = '?';
            li.setAttribute('aria-label', EN ? (d.s ? 'Secret, not found yet' : 'Not found yet') : d.s ? 'Secret, noch nicht entdeckt' : 'Noch nicht entdeckt');
          }
          // A found figure leads to its product page; an empty field stays a plain
          // question mark (a link would give away which figure is still missing).
          if (gefunden.has(d.h) && d.u) {
            const link = document.createElement('a');
            link.className = 'mbb-box__slot-link';
            link.href = d.u;
            link.append(feld, thema, geheim);
            li.append(link);
          } else {
            li.append(feld, thema, geheim);
          }
          return li;
        }),
      );
    };
    zeigeSammlung(null);
    // Announce the count from now on (finds, emptying, undo) — not the restored count at load.
    if (sammlungText) sammlungText.setAttribute('aria-live', 'polite');
    if (leeren) {
      leeren.addEventListener('click', () => {
        if (rueckgabe) {
          gefunden = rueckgabe.vorher;
          vergissRueckgabe();
        } else {
          if (!gefunden.size) return;
          const vorher = gefunden;
          gefunden = new Set();
          rueckgabe = {
            vorher,
            uhr: setTimeout(() => {
              rueckgabe = null;
              zeigeLeeren();
            }, 8000),
          };
        }
        merke();
        zeigeSammlung(null);
        // The fields just emptied fade back to their question marks (fresh elements, so it runs every time).
        if (rueckgabe && slots) slots.classList.add('ist-geleert');
      });
    }

    const dekodiere = (img, src) => {
      img.src = src;
      return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
    };
    // Shows a hidden element, then sets its class one frame later so the transition runs.
    const auftritt = (el, klasse) => {
      el.hidden = false;
      if (ruhig.matches) el.classList.add(klasse);
      else requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add(klasse)));
    };
    const pause = (ms) => (ruhig.matches ? Promise.resolve() : warte(ms));
    // The lid swings open with a little overshoot (100° and back from ~110°).
    const klappeAuf = (dauer) =>
      new Promise((fertig) => {
        if (!deckel) return fertig();
        const ende = (100 * Math.PI) / 180;
        const start = performance.now();
        const schritt = (jetzt) => {
          const t = klemme((jetzt - start) / dauer);
          const k = 1.25;
          const zurueck = 1 + (k + 1) * Math.pow(t - 1, 3) + k * Math.pow(t - 1, 2);
          deckel.zeichne(ende * zurueck);
          if (t < 1) requestAnimationFrame(schritt);
          else fertig();
        };
        requestAnimationFrame(schritt);
      });
    // Where the burst starts: the middle of the opening, in stage pixels.
    const knallOrt = () => {
      const rb = buehne.getBoundingClientRect();
      const rd = dose.getBoundingClientRect();
      const m = deckel ? deckel.mitte : { x: 500, y: 150 };
      return { x: rd.left - rb.left + (m.x / 1000) * rd.width, y: rd.top - rb.top + (m.y / 1000) * rd.height, s: rd.width };
    };

    // A secret announces itself: „SECRET“ shoots out of the opening with the burst,
    // hangs big above the box, then settles on the right of the figure and stays.
    // Its resting place is CSS; the flight is a transform offset from there.
    const secretFlug = (o) => {
      if (!secretWort) return;
      secretWort.hidden = false;
      if (ruhig.matches || !o) return;
      const rb = buehne.getBoundingClientRect();
      const rw = secretWort.getBoundingClientRect();
      const zx = rw.left + rw.width / 2 - rb.left;
      const zy = rw.top + rw.height / 2 - rb.top;
      const von = { x: o.x - zx, y: o.y - zy };
      // Hangs above the box, but inside the stage: the title is close above it.
      const oben = { x: rb.width / 2 - zx, y: Math.max(o.y - o.s * 0.42, rw.height) - zy };
      const lage = (p, k) => `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px) rotate(-8deg) scale(${k})`;
      secretWort.animate(
        [
          { transform: lage(von, 0.2), opacity: 0 },
          { transform: lage(oben, 1.35), opacity: 1, offset: 0.22, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
          { transform: lage(oben, 1.15), opacity: 1, offset: 0.55, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
          { transform: lage({ x: 0, y: 0 }, 1), opacity: 1 },
        ],
        { duration: 1500, delay: 120, fill: 'backwards' },
      );
    };

    // The audio channel is opened ahead, when the stage comes near.
    if (klang.moeglich && buehne && 'IntersectionObserver' in window) {
      const nah = new IntersectionObserver(
        (eintraege) => {
          if (!eintraege.some((e) => e.isIntersecting)) return;
          nah.disconnect();
          klang.vorbereiten();
        },
        { rootMargin: '400px 0px' },
      );
      nah.observe(buehne);
      // In view, after a click anywhere on the page, the channel is started too.
      new IntersectionObserver((eintraege) => {
        if (eintraege.some((e) => e.isIntersecting)) klang.wecken();
      }, { threshold: 0.5 }).observe(buehne);
    }
    knopf.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') klang.wecken();
    });
    // A mouse press is already a user action — a head start of the release.
    knopf.addEventListener('pointerdown', () => klang.wecken());
    knopf.addEventListener('focus', () => klang.wecken());
    // Sound on/off, offered only where the browser can play the sounds.
    if (tonKnopf && klang.moeglich) {
      const zeigeTon = () => {
        tonKnopf.setAttribute('aria-pressed', String(klang.an));
        tonKnopf.title = EN ? (klang.an ? 'Turn sound off' : 'Turn sound on') : klang.an ? 'Ton ausschalten' : 'Ton einschalten';
      };
      zeigeTon();
      tonKnopf.hidden = false;
      document.addEventListener('mbb-ton', zeigeTon);
      tonKnopf.addEventListener('click', () => {
        klang.schalte(!klang.an);
        if (klang.an) klang.spiele('ping');
      });
    }

    knopf.addEventListener('click', async () => {
      if (root.classList.contains('ist-offen') || knopf.dataset.laeuft) return;
      knopf.dataset.laeuft = '1';
      // Still inside the click: the one moment a browser lets audio start.
      klang.bereit();
      const d = ziehe();
      // A figure without manufacturer image stays in its bag: the bag photo is the reveal.
      const mitRiss = Boolean(d.b && d.f);
      const geladen = [dekodiere(figurBild, d.b || d.f)];
      figurBild.alt = `${linie ? linie.textContent : serieName} ${d.n}${d.i ? (EN ? ' (AI-generated image)' : ' (mit KI erzeugtes Bild)') : ''}`;
      if (d.c && karte) {
        geladen.push(dekodiere(karteBild, d.c));
        karteBild.alt = `Begleitkarte: ${d.n}`;
      }
      if (mitRiss && tuete) tueteBilder.forEach((img) => geladen.push(dekodiere(img, d.f)));
      deckelQuelle();
      const bereit = Promise.all(geladen);
      if (!ruhig.matches) {
        // Only the first click of a page view can wait here: for the sound channel,
        // so the knocks meet the wobble.
        await klang.laeuft(500);
        knopf.classList.add('wackelt');
        klang.spiele('ruetteln');
        await Promise.all([warte(650), bereit]);
        knopf.classList.remove('wackelt');
        // 1 · the lid swings open, a small burst comes out of the box.
        knopf.classList.add('oeffnet');
        const auf = klappeAuf(560);
        await warte(200);
        if (deckelSvg) deckelSvg.classList.add('glueht');
        if (funken && buehne && dose) {
          const o = knallOrt();
          knall(funken, o.x, o.y, o.s);
          if (d.s) secretFlug(o);
        }
        klang.spiele('knall');
        klang.spiele('gloeckchen', 0.05, d.s);
        if (d.s) klang.spiele('secretFlug', 0.12);
        root.classList.add('ist-offen');
        await auf;
      } else {
        await bereit;
        root.classList.add('ist-offen');
        if (d.s) secretFlug(null);
        // Without motion only the bells: the knocks and the rip had nothing to go with.
        klang.spiele('gloeckchen', 0, d.s);
      }
      // 2 · the companion card shoots out and stays leaning beside the figure.
      if (d.c && karte) auftritt(karte, 'ist-raus');
      await pause(220);
      knopf.classList.add('ist-weg');
      knopf.setAttribute('aria-hidden', 'true');
      knopf.tabIndex = -1;
      // 3 · the bag rises out of the box, its top strip tears off, the bag drops away.
      if (mitRiss && tuete) {
        auftritt(tuete, 'ist-da');
        await pause(900);
        tuete.classList.add('reisst');
        klang.spiele('riss');
        await pause(160);
      }
      // 4 · the figure comes out — an AI image only with the EU label beside it.
      auftritt(figur, 'ist-da');
      if (d.s && !ruhig.matches) klang.spiele('secretGlocke');
      if (kiLabel) kiLabel.hidden = !d.i;
      if (mitRiss && tuete) {
        warte(ruhig.matches ? 0 : 700).then(() => {
          if (!tuete.classList.contains('reisst')) return;
          tuete.hidden = true;
          tuete.classList.remove('ist-da', 'reisst');
        });
      }
      secretEl.toggleAttribute('data-aus', !d.s);
      nameEl.textContent = d.n;
      quoteEl.textContent = d.i
        ? EN
          ? 'Not shown on the box. Image created using artificial intelligence, not a photograph of the figure.'
          : 'Nicht auf der Box abgebildet. Bild mit KI erzeugt, keine Fotografie der Figur.'
        : d.s
        ? EN
          ? `Not even the box shows the secrets of the ${serieName}.`
          : `Secrets der ${serieName} zeigt nicht einmal die Box.`
        : d.q > 0
          ? quote(d.q)
          : ' ';
      link.href = d.u;
      if (figurLink) figurLink.href = d.u;
      linkText.textContent = EN ? (d.v ? 'Buy this figure' : 'View the figure') : d.v ? 'Einzeln kaufen' : 'Zur Figur';
      if (intro) intro.setAttribute('data-aus', '');
      ergebnis.removeAttribute('data-leer');
      const neu = !gefunden.has(d.h);
      vergissRueckgabe();
      gefunden.add(d.h);
      merke();
      zeigeSammlung(neu ? d.h : null);
      delete knopf.dataset.laeuft;
      nochmal.focus({ preventScroll: true });
    });
    nochmal.addEventListener('click', () => {
      figur.classList.remove('ist-da');
      figur.hidden = true;
      if (karte) {
        karte.classList.remove('ist-raus');
        karte.hidden = true;
      }
      if (tuete) {
        tuete.classList.remove('ist-da', 'reisst');
        tuete.hidden = true;
      }
      if (deckel) deckel.zeichne(0);
      if (deckelSvg) deckelSvg.classList.remove('glueht');
      knopf.classList.remove('ist-weg', 'oeffnet');
      knopf.removeAttribute('aria-hidden');
      knopf.tabIndex = 0;
      root.classList.remove('ist-offen');
      if (kiLabel) kiLabel.hidden = true;
      if (secretWort) {
        secretWort.getAnimations().forEach((a) => a.cancel());
        secretWort.hidden = true;
      }
      leer();
      knopf.focus({ preventScroll: true });
    });
  };

  /* ── 6 · Hologramm-Karte ────────────────────────────────────────── */
  module.hologramm = (root) => {
    const karte = root.querySelector('[data-karte]');
    const innen = root.querySelector('[data-innen]');
    if (!karte || !innen) return;
    const vorne = root.querySelector('[data-vorne]');
    const hinten = root.querySelector('[data-hinten]');
    const quelle = root.querySelector('[data-stuecke]');
    let stuecke = [];
    try {
      stuecke = JSON.parse(quelle ? quelle.textContent : '[]');
    } catch (e) {
      stuecke = [];
    }
    // Back side only: nothing to turn.
    if (stuecke.length === 0) return;
    const idEl = root.querySelector('[data-id]');
    const produkt = root.querySelector('[data-produkt]');
    const produktBild = root.querySelector('[data-produkt-bild]');
    const produktName = root.querySelector('[data-produkt-name]');
    const produktMeta = root.querySelector('[data-produkt-meta]');
    const produktNeu = root.querySelector('[data-produkt-neu]');
    const drehen = root.querySelector('[data-drehen]');
    const naechstes = root.querySelector('[data-naechstes]');
    const tonKnopf = root.querySelector('[data-ton]');
    let nr = 0;
    let gedreht = false;
    let ruheBis = 0;

    // Sounds, chosen by ear from three variants each (Betreiber 02.11.2026), played
    // through the blindbox's engine. The turn is front-loaded (700 ms,
    // cubic-bezier(0.22, 1, 0.36, 1): 96° after 100 ms, 147° after 200 ms), so is
    // the swish. r = 1 turns to the back, −1 to the front: pan and run mirror.
    // The first sound of a page waits for the browser to open its audio output
    // (up to 0.7 s); the engine then hands a start time before the context's zero
    // and skips everything before „now“ — a sound this short would stay silent
    // (measured: the first turn made no sound at all). It plays late instead.
    const ab = (t) => Math.max(t, 0.05);
    klang.neu('kartenSchwung', (t, w, r) => {
      t = ab(t);
      // The fingertip lets go …
      w.rausch(t, 'highpass', 2600, 0.7, 0.098, 0.001, 0.012, -0.2 * r);
      // … the air swishes, fastest where the card turns fastest …
      const a = w.rausch(t + 0.004, 'bandpass', 900, 0.8, 0.21, 0.03, 0.34, -0.3 * r);
      a.frequency.setValueAtTime(900, t);
      a.frequency.exponentialRampToValueAtTime(3000, t + 0.07);
      a.frequency.exponentialRampToValueAtTime(1100, t + 0.4);
      const b = w.rausch(t + 0.05, 'bandpass', 2400, 1.2, 0.112, 0.03, 0.3, 0.35 * r);
      b.frequency.setValueAtTime(2400, t + 0.05);
      b.frequency.exponentialRampToValueAtTime(1000, t + 0.38);
      // … with a little air pressure under it.
      w.ton(t, 'sine', 170, 75, 0.14, 0.049, 0.01, 0.18);
    });
    // The foil passes the light: a glassy run, upwards to the back, downwards to the front.
    klang.neu('kartenSchimmer', (t, w, r) => {
      t = ab(t);
      const lauf = [2093, 2637.02, 3135.96, 3951.07, 4698.63];
      if (r < 0) lauf.reverse();
      lauf.forEach((f, i) => w.glocke(t + 0.03 + i * 0.026, f, 0.3, (-0.5 + i * 0.25) * r, 0.4));
    });
    // A number counter rattling onto the next hologram ID, the last digit settles —
    // the digits on the card roll in the same ticks (TAKT below, in ms).
    const TAKT = [0, 28, 58, 90, 126, 168, 220, 285];
    klang.neu('zaehlwerk', (t, w) => {
      t = ab(t);
      TAKT.slice(0, -1).forEach((ms, i) => {
        const s = 0.75 + (0.25 * i) / (TAKT.length - 1);
        w.rausch(t + ms / 1000, 'bandpass', w.zufall(2900, 3500), 4, 0.16 * s, 0.0008, 0.012, 0.15);
        w.ton(t + ms / 1000, 'sine', 2200, 0, 0, 0.025 * s, 0.0008, 0.01, 0.15);
      });
      const ende = t + TAKT[TAKT.length - 1] / 1000;
      w.rausch(ende, 'bandpass', 2600, 3, 0.22, 0.0008, 0.02, 0.1);
      w.ton(ende, 'sine', 900, 600, 0.02, 0.07, 0.001, 0.04, 0.1);
    });
    // With reduced motion nothing turns: no swish, the run stays.
    const klingtDrehen = () => {
      const r = gedreht ? 1 : -1;
      if (!ruhig.matches) klang.spiele('kartenSchwung', 0, r);
      klang.spiele('kartenSchimmer', 0, r);
    };
    // Sound on/off, one setting with the blindbox ('mbb-ton' event keeps both switches in step).
    if (tonKnopf && klang.moeglich) {
      const zeigeTon = () => {
        tonKnopf.setAttribute('aria-pressed', String(klang.an));
        tonKnopf.title = EN ? (klang.an ? 'Turn sound off' : 'Turn sound on') : klang.an ? 'Ton ausschalten' : 'Ton einschalten';
      };
      zeigeTon();
      tonKnopf.hidden = false;
      document.addEventListener('mbb-ton', zeigeTon);
      tonKnopf.addEventListener('click', () => {
        klang.schalte(!klang.an);
        if (klang.an) klang.spiele('ping');
      });
    }

    // The next piece's digits roll like the counter they sound like (Betreiber
    // 02.11.2026): each digit is a window over a strip of its old digit, random
    // digits and the new one, rolling up one place per interval of TAKT; they land
    // one after another from left to right (ticks 3 to 7), the last with the final
    // click. Afterwards the number is plain text again. Screen readers get the new
    // number at once, the strips are hidden from them.
    let idJetzt = '';
    let rollt = [];
    const setzeId = (neu, verzoegerung) => {
      rollt.forEach((a) => a.cancel());
      rollt = [];
      const alt = idJetzt;
      idJetzt = neu;
      if (verzoegerung == null || !alt || ruhig.matches) {
        idEl.textContent = neu;
        return;
      }
      const zufallsziffer = () => String(Math.floor(Math.random() * 10));
      const ziffer = (c) => c >= '0' && c <= '9';
      const anzahl = [...neu].filter(ziffer).length;
      const dauer = TAKT[TAKT.length - 1] + 20;
      const sichtbar = document.createElement('span');
      sichtbar.setAttribute('aria-hidden', 'true');
      let k = 0;
      [...neu].forEach((c, i) => {
        const rad = document.createElement('span');
        rad.className = 'mbb-karte__rad';
        sichtbar.append(rad);
        if (!ziffer(c)) {
          rad.textContent = c;
          return;
        }
        // Steps until this wheel lands (3 … 7, left to right).
        const landet = 3 + Math.round((k++ * 4) / Math.max(1, anzahl - 1));
        const folge = [ziffer(alt[i] || '') ? alt[i] : zufallsziffer()];
        for (let s = 1; s < landet; s++) folge.push(zufallsziffer());
        folge.push(c);
        const band = document.createElement('span');
        band.textContent = folge.join('\n');
        rad.append(band);
        // The wheel turns without stopping, one digit per interval of TAKT, and each
        // tick is a digit clicking into place — the intervals grow, so it slows down
        // by itself; the last step eases out. The explicit last frame keeps the strip
        // on the new digit (WAAPI would otherwise end on the starting value).
        const bild = [];
        for (let s = 0; s < landet; s++) {
          bild.push({ transform: `translateY(${-s}em)`, offset: TAKT[s] / dauer, easing: s === landet - 1 ? 'cubic-bezier(0.2, 0.7, 0.3, 1)' : 'linear' });
        }
        bild.push({ transform: `translateY(${-landet}em)`, offset: TAKT[landet] / dauer });
        bild.push({ transform: `translateY(${-landet}em)`, offset: 1 });
        rollt.push(band.animate(bild, { duration: dauer, delay: verzoegerung * 1000, fill: 'forwards' }));
      });
      const vorlesen = document.createElement('span');
      vorlesen.className = 'visually-hidden';
      vorlesen.textContent = neu;
      idEl.replaceChildren(vorlesen, sichtbar);
      const diese = rollt;
      Promise.all(diese.map((a) => a.finished)).then(
        () => {
          if (rollt !== diese) return;
          idEl.textContent = neu;
          rollt = [];
        },
        () => {},
      );
    };

    const zeige = (i, rollen) => {
      const s = stuecke[i];
      setzeId(s.i, rollen);
      produkt.href = s.u;
      produktName.textContent = s.n;
      if (produktMeta) produktMeta.textContent = s.m ? `${s.m} · ${EN ? 'in stock' : 'auf Lager'}` : EN ? 'in stock' : 'auf Lager';
      if (produktNeu) {
        produktNeu.textContent = s.e || '';
        produktNeu.hidden = !s.e;
      }
      if (s.b) {
        produktBild.src = s.b;
        produktBild.hidden = false;
      } else {
        produktBild.hidden = true;
      }
    };
    const seite = () => {
      karte.classList.remove('folgt');
      ruheBis = performance.now() + 800;
      karte.classList.toggle('ist-gedreht', gedreht);
      vorne.inert = gedreht;
      hinten.inert = !gedreht;
      // No aria-pressed: the button's label itself changes with the side (CSS).
    };
    zeige(0);
    seite();
    if (naechstes && stuecke.length < 2) naechstes.hidden = true;
    drehen.addEventListener('click', () => {
      gedreht = !gedreht;
      seite();
      klingtDrehen();
    });
    karte.addEventListener('click', (e) => {
      if (e.target.closest('a')) return;
      gedreht = !gedreht;
      seite();
      klingtDrehen();
    });
    naechstes?.addEventListener('click', () => {
      nr = (nr + 1) % stuecke.length;
      const zurueck = gedreht;
      if (gedreht) {
        gedreht = false;
        seite();
        klingtDrehen();
      }
      // Turning back first: the counter starts once the swish has passed its peak.
      const versatz = zurueck ? 0.12 : 0;
      klang.spiele('zaehlwerk', versatz);
      zeige(nr, versatz);
    });

    if (ruhig.matches) return;
    const setze = (x, y, kipp = 1) => {
      innen.style.setProperty('--ry', `${(x - 0.5) * 26 * kipp}deg`);
      innen.style.setProperty('--rx', `${(0.5 - y) * 18 * kipp}deg`);
      innen.style.setProperty('--mx', `${x * 100}%`);
      innen.style.setProperty('--my', `${y * 100}%`);
      // The seal's motifs: bear to the right, „@“ to the left; 0 (both faint) when the card rests.
      innen.style.setProperty('--kx', ((x - 0.5) * 2 * Math.min(1, kipp * 2)).toFixed(3));
    };
    let zeigt = false;
    let sichtbar = false;
    karte.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      const r = karte.getBoundingClientRect();
      zeigt = true;
      if (performance.now() > ruheBis) karte.classList.add('folgt');
      setze(klemme((e.clientX - r.left) / r.width), klemme((e.clientY - r.top) / r.height));
    });
    karte.addEventListener('pointerleave', () => {
      zeigt = false;
      karte.classList.remove('folgt');
      setze(0.5, 0.4, 0);
    });
    // Phones without pointer: the card follows the device (Android; iOS asks for
    // permission, so it simply idles). Not from the angles themselves (Betreiber
    // 03.11.2026: on the phone it jumped between the ends instead of turning
    // gradually): gamma flips by up to 180° when the phone is held near upright,
    // and a fixed rest position at 45° put a steeper grip at the stop already.
    // Instead the direction of gravity in the screen plane, which has no such
    // jump, measured against a rest position that follows the grip within a few
    // seconds — so it is the movement that turns the foil — and smoothed per frame.
    let schwere = null;
    let ruhelage = null;
    let neigung = null;
    window.addEventListener(
      'deviceorientation',
      (e) => {
        if (e.gamma == null || e.beta == null) return;
        const b = (e.beta * Math.PI) / 180;
        const g = (e.gamma * Math.PI) / 180;
        const gx = Math.cos(b) * Math.sin(g);
        const gy = -Math.sin(b);
        // Landscape: the screen's axes are the device's turned by the screen angle.
        const w = (((screen.orientation && screen.orientation.angle) || window.orientation || 0) * Math.PI) / 180;
        schwere = { x: gx * Math.cos(w) + gy * Math.sin(w), y: gy * Math.cos(w) - gx * Math.sin(w) };
        if (!ruhelage) ruhelage = { ...schwere };
      },
      { passive: true },
    );
    // About 15° of tilt from the rest position turn the foil all the way.
    const EMPFINDLICH = 1.9;
    // Idle: a slow sway so the foil shows itself.
    const tick = (t) => {
      if (!sichtbar) return;
      if (!zeigt && performance.now() > ruheBis) {
        karte.classList.add('folgt');
        if (schwere) {
          ruhelage.x = mische(ruhelage.x, schwere.x, 0.006);
          ruhelage.y = mische(ruhelage.y, schwere.y, 0.006);
          const ziel = {
            x: klemme(0.5 + (schwere.x - ruhelage.x) * EMPFINDLICH),
            y: klemme(0.45 - (schwere.y - ruhelage.y) * EMPFINDLICH),
          };
          neigung = neigung ? { x: mische(neigung.x, ziel.x, 0.18), y: mische(neigung.y, ziel.y, 0.18) } : ziel;
          setze(neigung.x, neigung.y);
        } else setze(0.5 + Math.sin(t / 1700) * 0.35, 0.45 + Math.cos(t / 2300) * 0.25, 0.5);
      }
      requestAnimationFrame(tick);
    };
    beobachte(root, (s) => {
      const war = sichtbar;
      sichtbar = s;
      if (s && !war) requestAnimationFrame(tick);
    });
  };

  /* ── 7 · Kurz erklärt: the sizes to scale on one floor ──────────── */
  module.wissen = (root) => {
    const buehne = root.querySelector('.mbb-ws__buehne');
    const kaesten = buehne ? [...buehne.querySelectorAll('.mbb-ws__figur')] : [];
    // Each box is as tall as its size (CSS); the visible figure fills exactly that height.
    const richte = () =>
      kaesten.forEach((box) => {
        const img = box.querySelector('img');
        if (!img || !box.offsetWidth) return;
        umriss(img).then((u) => {
          if (!u || !box.offsetWidth) return;
          stelle(img, u, { hoehe: box.clientHeight, mitte: box.clientWidth / 2, boden: box.clientHeight });
        });
      });
    if (buehne) new ResizeObserver(richte).observe(buehne);

    // "Im Zulauf": the figures of a tile at the same visible height, each in the
    // middle of its share of the width, on one floor.
    // Every figure tile alike: the visible figure 76 % of the tile high, feet on
    // a floor 10 % above the bottom edge, centred on its share of the width.
    root.querySelectorAll('.mbb-ws__bild--gruppe, .mbb-ws__bild--figur').forEach((kachel) => {
      const bilder = [...kachel.querySelectorAll('img')];
      const stelleAlle = () => {
        const b = kachel.clientWidth;
        const h = kachel.clientHeight;
        if (!b || !h) return;
        bilder.forEach((img, i) =>
          umriss(img).then((u) => {
            if (u) stelle(img, u, { hoehe: h * 0.76, mitte: (b * (i + 0.5)) / bilder.length, boden: h * 0.9 });
          }),
        );
      };
      new ResizeObserver(stelleAlle).observe(kachel);
    });
  };

  /* ── 8 · Versprechen: the way of an order, dated for an order placed now ── */
  // Dates come from the shipping calendar in assets/mbb-versandkalender.js (one
  // source with the product page, A-616): cut-off Mon–Fri 14:00 and Sat 11:00
  // Berlin time, no shipping on Sundays, public holidays of Baden-Württemberg and
  // 31.12. Saturday counts as a delivery day only in Germany (DHL); abroad most
  // parcels go with UPS Standard, which does not deliver on Saturdays.
  // The shipping calendar lives in assets/mbb-versandkalender.js (A-616); its tag may
  // come after this script's, so wait for it when it is not there yet.
  const mitKalender = (fn) => {
    if (window.mbbVersandkalender) fn();
    else document.addEventListener('mbb:versandkalender', fn, { once: true });
  };

  module.versprechen = (root) => {
    let texte = {};
    try {
      texte = JSON.parse(root.querySelector('[data-vs-texte]').textContent);
    } catch (e) {
      return;
    }
    const de = texte.de !== false;
    const weg = root.querySelector('.mbb-vs__weg');

    // The route draws once, station by station as each comes into view: in
    // the row (from 990 px) all five arrive together and the CSS staggers
    // them, down the left edge the line follows the scrolling. Not if the
    // route is already on screen at start — then it is simply there.
    if (weg && !ruhig.matches && weg.getBoundingClientRect().top > window.innerHeight) {
      const halte = [...weg.children];
      root.classList.add('wartet');
      const io = new IntersectionObserver(
        (eintraege) =>
          eintraege.forEach((e) => {
            if (!e.isIntersecting) return;
            io.unobserve(e.target);
            e.target.classList.add('ist-da');
            const davor = halte[halte.indexOf(e.target) - 1];
            if (davor) davor.classList.add('ist-weiter');
          }),
        { rootMargin: '0px 0px -15% 0px' }
      );
      halte.forEach((h) => io.observe(h));
    }

    // „So prüfen wir“: jump to the hologram card when it is on the same page.
    const echtheit = root.querySelector('[data-vs-echtheit]');
    const holo = document.querySelector('[data-mbb="hologramm"]');
    if (echtheit && holo) {
      echtheit.addEventListener('click', (e) => {
        e.preventDefault();
        holo.scrollIntoView({ behavior: ruhig.matches ? 'auto' : 'smooth', block: 'start' });
      });
    }

    // Money in the cart currency, in the shop's own format: the server renders
    // 1.234.567,89 with that format, the pattern is read back from it.
    const muster = /^([^\d]*)1([^\d]*)234[^\d]*56\d(?:([^\d]+)\d\d)?([^\d]*)$/.exec(texte.betragMuster || '');
    const betrag = (cent) => {
      if (!muster) return (cent / 100).toFixed(2);
      const [, vor, tausender, komma, nach] = muster;
      const ganz = Math.floor(cent / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, tausender);
      // No break between amount and currency sign.
      return (vor + ganz + (komma ? komma + String(cent % 100).padStart(2, '0') : '') + nach).replace(/ /g, ' ');
    };

    const meter = root.querySelector('[data-meter]');
    if (meter) {
      const ziel = Number(meter.dataset.meter);
      const fuellung = meter.querySelector('.mbb-vs__balken > span');
      const text = meter.querySelector('[data-meter-text]');
      const zeige = (summe) => {
        const rest = Math.max(0, ziel - summe);
        meter.classList.toggle('ist-voll', rest === 0);
        fuellung.style.transform = `scaleX(${klemme(summe / ziel)})`;
        text.textContent = rest === 0 ? texte.meterVoll : texte.meterRest.replace('[betrag]', betrag(rest));
      };
      const hole = () =>
        fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart.js`)
          .then((r) => r.json())
          .then((c) => zeige(c.total_price))
          .catch(() => {});
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, hole);
      document.addEventListener('cart:updated', hole);
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) hole();
      });
    }

    // Dates. With the holidays setting filled the server wording stays.
    if (root.hasAttribute('data-pause')) return;
    const [transitMin, transitMax] = (root.dataset.transit || '1,3').split(',').map(Number);
    const samstag = root.hasAttribute('data-samstag');
    const feld = (name) => root.querySelector(`[data-wann="${name}"]`);
    const uhr = root.querySelector('[data-uhr]');
    const uhrText = root.querySelector('[data-uhr-text]');
    const wtKurz = de ? ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monateEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const datum = (t) => {
      const d = new Date(t);
      return de ? `${d.getUTCDate()}.${d.getUTCMonth() + 1}.` : `${d.getUTCDate()} ${monateEn[d.getUTCMonth()]}`;
    };
    const tagMitWt = (t) => `${wtKurz[new Date(t).getUTCDay()]} ${datum(t)}`;
    const uhrzeit = (h) => (de ? `${h}:00 Uhr` : h > 12 ? `${h - 12} pm` : `${h} am`);
    const setze = (el, s) => {
      if (el && el.textContent !== s) el.textContent = s;
    };

    const tick = () => {
      if (document.hidden) return;
      const { TAG, berlinJetzt, versandtagFuer, schlussStunde, plusArbeitstage } = window.mbbVersandkalender;
      const jetzt = berlinJetzt();
      const versand = versandtagFuer(jetzt);
      if (versand === null) return;
      const inTagen = Math.round((versand - jetzt.tag) / TAG);
      const schluss = schlussStunde(versand);

      let bestellen;
      if (inTagen === 0) bestellen = de ? `Heute bis ${uhrzeit(schluss)}` : `Today until ${uhrzeit(schluss)}`;
      else if (inTagen === 1) bestellen = de ? `Bis morgen, ${uhrzeit(schluss)}` : `By tomorrow, ${uhrzeit(schluss)}`;
      else bestellen = de ? `Bis ${wtKurz[new Date(versand).getUTCDay()]}, ${uhrzeit(schluss)}` : `By ${wtKurz[new Date(versand).getUTCDay()]}, ${uhrzeit(schluss)}`;
      if (inTagen > 6) bestellen = de ? `Bis ${tagMitWt(versand)}, ${uhrzeit(schluss)}` : `By ${tagMitWt(versand)}, ${uhrzeit(schluss)}`;
      setze(feld('bestellen'), bestellen);

      // Minutes to the cut-off (calendar days × 1440; a DST night is off by
      // an hour, the product page has the same simplification).
      const rest = inTagen * 1440 + schluss * 60 - jetzt.minute;
      const std = Math.floor(rest / 60);
      const min = rest % 60;
      // Hours and minutes up to two days (after the cut-off on a weekday it
      // reads „Noch 23 Std. 59 Min.“, not „1 Tag 0 Std.“), days beyond.
      const tage = Math.floor(std / 24);
      let noch;
      if (de) noch = std >= 48 ? `Noch ${tage} Tage${std % 24 ? ` ${std % 24} Std.` : ''}` : std > 0 ? `Noch ${std} Std. ${min} Min.` : `Noch ${min} Min.`;
      else noch = std >= 48 ? `${tage} days${std % 24 ? ` ${std % 24} h` : ''} left` : std > 0 ? `${std} h ${min} min left` : `${min} min left`;
      setze(uhrText, noch);
      if (uhr) uhr.classList.add('ist-an');

      const versandText =
        inTagen === 0 ? (de ? `Heute, ${datum(versand)}` : `Today, ${datum(versand)}`)
        : inTagen === 1 ? (de ? `Morgen, ${datum(versand)}` : `Tomorrow, ${datum(versand)}`)
        : tagMitWt(versand);
      setze(feld('versand'), versandText);

      const frueh = plusArbeitstage(versand, transitMin, samstag);
      const spaet = plusArbeitstage(versand, transitMax, samstag);
      setze(feld('ankunft'), `${tagMitWt(frueh)}–${tagMitWt(spaet)}`);
      // 30 days from receipt; counted from the earliest delivery, so the
      // date shown is the earliest possible end — „at least“.
      const rueckgabe = frueh + 30 * TAG;
      setze(feld('rueckgabe'), de ? `Bis mindestens ${datum(rueckgabe)}` : `Until at least ${datum(rueckgabe)}`);
    };
    mitKalender(() => {
      tick();
      setInterval(tick, 20000);
      document.addEventListener('visibilitychange', tick);
    });
  };

  /* ── Start ──────────────────────────────────────────────────────── */
  const starte = (bereich) => {
    bereich.querySelectorAll('[data-mbb]').forEach((el) => {
      if (el.mbbGestartet) return;
      const f = module[el.dataset.mbb];
      if (!f) return;
      el.mbbGestartet = true;
      f(el);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => starte(document));
  else starte(document);
  document.addEventListener('shopify:section:load', (e) => starte(e.target));
})();
